/**
 * PythonCompleteAlgorithms.jsx
 * ─────────────────────────────────────────────────────────────
 * Single-file React + MUI study guide covering:
 *   11 Python Data Structures:
 *     list, tuple, set, frozenset, dict,
 *     collections.deque, collections.defaultdict,
 *     collections.Counter, collections.OrderedDict,
 *     + custom Stack class, custom Queue class
 *   6 Algorithm Patterns:
 *     Binary Search, Sorting, Two Pointers,
 *     Sliding Window, Dict/Counter Patterns,
 *     Recursion & Backtracking
 *
 * Clean Code Principles:
 *   1. Meaningful Names   – every name tells you WHAT it is
 *   2. Single Responsibility – each component does ONE thing
 *   3. DRY  – all Python content in DATA constants at top
 *   4. Small Functions   – no function exceeds ~40 lines
 *   5. Comments for WHY  – explain reasoning, not mechanics
 * ─────────────────────────────────────────────────────────────
 */

// ── React core hooks ──────────────────────────────────────────
import { useState, useMemo } from "react";

// ── MUI layout & navigation ───────────────────────────────────
import {
    Box, Drawer, AppBar, Toolbar, Typography, IconButton,
    List, ListItem, ListItemButton, ListItemIcon, ListItemText,
    Divider, Tabs, Tab, Card, CardContent, Chip, Tooltip,
    Accordion, AccordionSummary, AccordionDetails,
    Button, useMediaQuery, CssBaseline
} from "@mui/material";

// ── MUI theming ───────────────────────────────────────────────
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";

// ── MUI icons ─────────────────────────────────────────────────
import MenuIcon        from "@mui/icons-material/Menu";
import CloseIcon       from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon       from "@mui/icons-material/Check";
import ExpandMoreIcon  from "@mui/icons-material/ExpandMore";
import CodeIcon        from "@mui/icons-material/Code";
import SchoolIcon      from "@mui/icons-material/School";
import FunctionsIcon   from "@mui/icons-material/Functions";
import ListAltIcon     from "@mui/icons-material/ListAlt";
import FilterListIcon  from "@mui/icons-material/FilterList";
import GridOnIcon      from "@mui/icons-material/GridOn";
import LockIcon        from "@mui/icons-material/Lock";
import LayersIcon      from "@mui/icons-material/Layers";
import LinkIcon        from "@mui/icons-material/Link";
import BookmarkIcon    from "@mui/icons-material/Bookmark";
import ViewStreamIcon  from "@mui/icons-material/ViewStream";
import TuneIcon        from "@mui/icons-material/Tune";
import BarChartIcon    from "@mui/icons-material/BarChart";
import SortIcon        from "@mui/icons-material/Sort";
import RepeatIcon      from "@mui/icons-material/Repeat";

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SECTION 1 — THEME
// All colours defined once here. Change a value → reskins
// the whole app automatically (Single Source of Truth).
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/**
 * APP_COLORS — central colour palette.
 * Every component reads from this object instead of
 * hardcoding hex strings, so the design stays consistent.
 */
const APP_COLORS = {
    background:  "rgba(16, 7, 32, 0.85)",  // deep purple-black canvas
    surface:     "rgba(26, 14, 52, 0.95)",  // card / panel background
    border:      "rgba(218,165,32,0.25)",   // goldenrod border (subtle)
    title:       "#DAA520",                 // goldenrod — all headings
    text:        "#FFFFFF",                 // primary text
    textMuted:   "rgba(255,255,255,0.55)",  // secondary / helper text
    success:     "#56d364",                 // Easy difficulty / O(1)
    warning:     "#ffa657",                 // Medium / O(log n)
    danger:      "#f85149",                 // Hard / O(n)
    drawerBg:    "rgba(10,4,22,0.98)",      // sidebar background
    codeBg:      "rgba(6,2,16,0.98)",       // code block background
    pyBlue:      "#3776ab",                 // Python brand blue
    pyYellow:    "#ffd343",                 // Python brand yellow
};

/**
 * CATEGORY_STYLE_MAP — maps a method category to a chip colour.
 * Centralised so every collection shares consistent styling.
 */
const CATEGORY_STYLE_MAP = {
    Insert:   { bg:"#0d3321", color:"#56d364" },
    Access:   { bg:"#0d2236", color:"#79c0ff" },
    Modify:   { bg:"#2d2000", color:"#e3b341" },
    Delete:   { bg:"#2d1a1a", color:"#f85149" },
    Info:     { bg:"#1e1333", color:"#bc8cff" },
    Order:    { bg:"#2a1d00", color:"#ffa657" },
    Convert:  { bg:"#002a2d", color:"#39c5cf" },
    Iterate:  { bg:"#0a1f2e", color:"#67e8f9" },
    Slice:    { bg:"#1a1a2e", color:"#e879f9" },
};

/** DIFFICULTY_COLOR_MAP — Easy/Medium/Hard chip colours. */
const DIFFICULTY_COLOR_MAP = {
    Easy:   APP_COLORS.success,
    Medium: APP_COLORS.warning,
    Hard:   APP_COLORS.danger,
};

/**
 * buildAppTheme — creates the MUI dark theme once.
 * useMemo in the root component prevents re-creation on render.
 */
const buildAppTheme = () => createTheme({
    palette: {
        mode: "dark",
        background: { default: APP_COLORS.background, paper: APP_COLORS.surface },
        primary:    { main: APP_COLORS.title },
        text:       { primary: APP_COLORS.text, secondary: APP_COLORS.textMuted },
    },
    typography: {
        // Monospace gives "code editor" feel throughout the whole app
        fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace",
    },
    components: {
        // Remove MUI's default gradient overlay so our rgba values show through
        MuiPaper:  { styleOverrides: { root:  { backgroundImage:"none" } } },
        MuiDrawer: { styleOverrides: { paper: { backgroundImage:"none" } } },
        MuiAppBar: { styleOverrides: { root:  { backgroundImage:"none" } } },
        MuiCard:   { styleOverrides: { root:  { backgroundImage:"none" } } },
    },
});

// Sidebar width in pixels — shared between desktop drawer and mobile drawer
const DRAWER_WIDTH = 228;
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SECTION 2 — DATA LAYER  (list, tuple, set, frozenset)
// Pure JS objects — no JSX or UI logic here.
// Every topic has the same shape so components are generic.
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

// ── list ──────────────────────────────────────────────────────
const LIST_DATA = {
    icon:"📋", color:"#f0883e",
    tagline:"Ordered, mutable, allows duplicates — Python's most versatile structure",
    badge:"list (built-in)",
    whenToUse:"Default ordered collection; stack via append/pop; most algorithm problems",
    declare:"my_list = []  # or  my_list = list()",
    complexity:{ get:"O(1)", append:"O(1)", insert:"O(n)", search:"O(n)" },
    note:"append/pop are O(1); insert/remove at arbitrary index are O(n) due to shifting",
    methods:[
        // INSERT
        { name:"append(x)",         cat:"Insert",  desc:"Adds x to the END — O(1) amortised. Most common list operation",         code:"lst = [1, 2]\nlst.append(3)\n# [1, 2, 3]" },
        { name:"insert(i, x)",      cat:"Insert",  desc:"Inserts x at index i — O(n) because elements shift right",               code:"lst.insert(0, 99)  # insert at head\n# [99, 1, 2, 3]" },
        { name:"extend(iterable)",  cat:"Insert",  desc:"Appends all items from an iterable — like += but explicit",               code:"lst.extend([4, 5])\n# [1, 2, 3, 4, 5]" },
        // ACCESS
        { name:"lst[i]",            cat:"Access",  desc:"Index access — O(1). Negative index: lst[-1] = last element",             code:"lst[0]   # first\nlst[-1]  # last\nlst[-2]  # second to last" },
        { name:"lst[start:stop:step]",cat:"Slice", desc:"Slice returns a NEW list — does NOT mutate original",                    code:"lst[1:4]    # indices 1,2,3\nlst[::-1]   # reversed copy\nlst[::2]    # every other" },
        // MODIFY
        { name:"lst[i] = x",        cat:"Modify",  desc:"In-place update at index i — O(1)",                                      code:"lst[0] = 99  # overwrite first element" },
        { name:"sort(key, reverse)", cat:"Order",  desc:"Sorts IN PLACE — uses Timsort O(n log n). Does NOT return a value!",     code:"lst.sort()                    # ascending\nlst.sort(reverse=True)        # descending\nlst.sort(key=lambda x: x[1]) # by 2nd element" },
        { name:"reverse()",         cat:"Modify",  desc:"Reverses list IN PLACE — O(n). Use [::-1] for a reversed COPY",          code:"lst.reverse()   # mutates original\ncopy = lst[::-1]  # new reversed list" },
        // DELETE
        { name:"pop(i=-1)",         cat:"Delete",  desc:"Removes and returns element at index i. Default=last — O(1) for end",    code:"last = lst.pop()    # removes last O(1)\nfirst = lst.pop(0)  # removes first O(n)" },
        { name:"remove(x)",         cat:"Delete",  desc:"Removes FIRST occurrence of x — O(n) scan. Raises ValueError if missing",code:"lst.remove(99)  # removes first 99\n# raises ValueError if not found" },
        { name:"del lst[i]",        cat:"Delete",  desc:"Deletes element at index, or entire slice, in-place",                    code:"del lst[0]      # delete first\ndel lst[1:3]    # delete slice" },
        { name:"clear()",           cat:"Delete",  desc:"Removes ALL elements — same as del lst[:] but more readable",            code:"lst.clear()  # []" },
        // INFO
        { name:"len(lst)",          cat:"Info",    desc:"Returns number of elements — O(1). Built-in function, not method",        code:"len([1, 2, 3])  # 3" },
        { name:"count(x)",          cat:"Info",    desc:"Counts occurrences of x — O(n) linear scan",                             code:"[1,2,2,3].count(2)  # 2" },
        { name:"index(x)",          cat:"Info",    desc:"Index of first occurrence — O(n). Raises ValueError if not found",        code:"lst.index(2)  # first position of 2" },
        { name:"in operator",       cat:"Info",    desc:"Membership test — O(n) for list, O(1) for set/dict",                     code:"3 in lst    # True\n9 not in lst # True" },
        // ITERATE
        { name:"for x in lst",      cat:"Iterate", desc:"Iterates each element in order — most Pythonic loop",                    code:"for x in lst:\n    print(x)" },
        { name:"enumerate(lst)",    cat:"Iterate", desc:"Yields (index, value) pairs — avoids manual counter variable",            code:"for i, v in enumerate(lst):\n    print(i, v)" },
        { name:"zip(a, b)",         cat:"Iterate", desc:"Pairs elements from multiple iterables — stops at shortest",             code:"for a, b in zip(lst1, lst2):\n    print(a, b)" },
        { name:"list comprehension",cat:"Iterate", desc:"Compact loop that builds a new list — more Pythonic than map/filter",    code:"squares = [x**2 for x in range(10)]\nevens   = [x for x in lst if x%2==0]" },
        // CONVERT
        { name:"sorted(lst)",       cat:"Convert", desc:"Returns a NEW sorted list — does NOT mutate. Works on any iterable",     code:"new = sorted(lst)             # new list\nnew = sorted(lst, reverse=True)  # descending" },
        { name:"list(iterable)",    cat:"Convert", desc:"Converts any iterable (tuple, set, range, str…) to a list",             code:"list((1,2,3))     # from tuple\nlist('hello')      # ['h','e','l','l','o']\nlist(range(5))     # [0,1,2,3,4]" },
        { name:"copy() / [:]",      cat:"Convert", desc:"Shallow copy — changes to copy don't affect original (for 1 level)",    code:"shallow = lst.copy()  # method\nshallow = lst[:]     # slice copy\n# For nested: use copy.deepcopy(lst)" },
    ],
    questions:[
        {
            title:"Find all duplicates in a list",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 Set tracking", tip:"add() on a set is O(1) — total algorithm is O(n) time, O(n) space.",
                    code:`nums = [1, 2, 3, 2, 4, 3, 5]

seen = set()
duplicates = set()

for n in nums:
    # If already seen, it's a duplicate
    if n in seen:
        duplicates.add(n)
    else:
        seen.add(n)

print(list(duplicates))  # [2, 3]` },
                { label:"🟠 Counter from collections", tip:"Counter counts all frequencies in one line — then filter > 1.",
                    code:`from collections import Counter

nums = [1, 2, 3, 2, 4, 3, 5]
freq = Counter(nums)

# Keep only elements appearing more than once
duplicates = [k for k, v in freq.items() if v > 1]
print(duplicates)  # [2, 3]` },
                { label:"🟢 list.count() approach", tip:"Simple but O(n²) — fine for small lists, slow for large ones.",
                    code:`nums = [1, 2, 3, 2, 4, 3, 5]

# count() scans the whole list each time → O(n²) total
duplicates = list({x for x in nums if nums.count(x) > 1})
print(duplicates)  # [2, 3]

# When to use: tiny lists where readability > performance` },
            ],
        },
        {
            title:"Rotate a list by k positions",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 Slice + concat", tip:"Python slicing is clean and readable. k % n handles k > len(lst).",
                    code:`def rotate_right(lst, k):
    n = len(lst)
    k = k % n  # handle k > n
    if k == 0:
        return lst[:]
    # Last k elements move to front
    return lst[-k:] + lst[:-k]

print(rotate_right([1,2,3,4,5], 2))  # [4, 5, 1, 2, 3]` },
                { label:"🟠 collections.deque rotate()", tip:"deque has a built-in rotate — negative = left, positive = right.",
                    code:`from collections import deque

def rotate_right(lst, k):
    d = deque(lst)
    d.rotate(k)      # positive = rotate right
    return list(d)

print(rotate_right([1,2,3,4,5], 2))  # [4, 5, 1, 2, 3]
# d.rotate(-2) would rotate LEFT` },
                { label:"🟢 Three reverses (O(1) space)", tip:"Reverse whole list, then each part. In-place, no extra memory.",
                    code:`def rotate_right(lst, k):
    n = len(lst)
    k = k % n

    def reverse(l, r):
        while l < r:
            lst[l], lst[r] = lst[r], lst[l]
            l += 1; r -= 1

    reverse(0, n-1)    # step 1: reverse entire list
    reverse(0, k-1)    # step 2: reverse first k elements
    reverse(k, n-1)    # step 3: reverse the rest
    return lst

print(rotate_right([1,2,3,4,5], 2))  # [4, 5, 1, 2, 3]` },
            ],
        },
        {
            title:"Flatten a nested list",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 itertools.chain.from_iterable", tip:"Built-in — fastest for single-level nesting. One liner.",
                    code:`import itertools

nested = [[1, 2], [3, 4], [5, 6]]
flat = list(itertools.chain.from_iterable(nested))
print(flat)  # [1, 2, 3, 4, 5, 6]

# One-liner with unpacking:
flat2 = [x for sub in nested for x in sub]
print(flat2)  # [1, 2, 3, 4, 5, 6]` },
                { label:"🟠 Recursive flatten (any depth)", tip:"isinstance() check recurses into any depth of nesting.",
                    code:`def flatten(lst):
    result = []
    for item in lst:
        if isinstance(item, list):
            result.extend(flatten(item))  # recurse
        else:
            result.append(item)
    return result

print(flatten([1, [2, [3, [4, [5]]]]]))
# [1, 2, 3, 4, 5]` },
                { label:"🟢 Stack-based flatten (no recursion)", tip:"Avoids recursion limit for very deeply nested lists.",
                    code:`def flatten(lst):
    stack = list(lst)   # copy to avoid mutating input
    result = []
    while stack:
        item = stack.pop()
        if isinstance(item, list):
            stack.extend(item)  # push inner items back
        else:
            result.append(item)
    return result[::-1]  # reverse because we popped from end

print(flatten([1, [2, [3, [4]]]]))  # [1, 2, 3, 4]` },
            ],
        },
    ],
};

// ── tuple ─────────────────────────────────────────────────────
const TUPLE_DATA = {
    icon:"📌", color:"#67e8f9",
    tagline:"Ordered, immutable sequence — faster and hashable compared to list",
    badge:"tuple (built-in)",
    whenToUse:"Immutable records; dict keys; function return values; named constants",
    declare:"my_tuple = ()  # or  my_tuple = tuple()",
    complexity:{ get:"O(1)", search:"O(n)", count:"O(n)", unpack:"O(n)" },
    note:"Tuples are IMMUTABLE — you cannot append, remove, or change elements after creation",
    methods:[
        { name:"t[i]",             cat:"Access",  desc:"Index access — O(1). Supports negative indices like list",          code:"t = (10, 20, 30)\nt[0]   # 10\nt[-1]  # 30" },
        { name:"t[start:stop]",    cat:"Slice",   desc:"Slice returns a NEW tuple — does not mutate",                       code:"t[1:3]   # (20, 30)\nt[::-1]  # (30, 20, 10) reversed" },
        { name:"count(x)",         cat:"Info",    desc:"Counts occurrences of x — O(n)",                                    code:"(1,2,2,3).count(2)  # 2" },
        { name:"index(x)",         cat:"Info",    desc:"Index of first occurrence — O(n). Raises ValueError if missing",    code:"(10,20,30).index(20)  # 1" },
        { name:"len(t)",           cat:"Info",    desc:"Number of elements — O(1). Built-in function",                      code:"len((1,2,3))  # 3" },
        { name:"in operator",      cat:"Info",    desc:"Membership test — O(n) linear scan",                                code:"20 in t   # True" },
        { name:"unpacking",        cat:"Access",  desc:"Assign tuple elements to variables — Pythonic and readable",        code:"a, b, c = (1, 2, 3)\nfirst, *rest = (1,2,3,4)  # star unpack\n# first=1, rest=[2,3,4]" },
        { name:"namedtuple",       cat:"Access",  desc:"Tuple with named fields — readable access without index magic",     code:"from collections import namedtuple\nPoint = namedtuple('Point', ['x','y'])\np = Point(3, 4)\np.x  # 3  (same as p[0])" },
        { name:"tuple(iterable)",  cat:"Convert", desc:"Converts any iterable to a tuple",                                  code:"tuple([1,2,3])   # from list\ntuple('abc')   # ('a','b','c')\ntuple(range(4))  # (0,1,2,3)" },
        { name:"list(t)",          cat:"Convert", desc:"Converts tuple to mutable list for modification",                   code:"lst = list(t)\nlst.append(99)\nt2 = tuple(lst)  # back to tuple" },
        { name:"t1 + t2",          cat:"Insert",  desc:"Concatenation returns a NEW tuple — does not mutate",               code:"(1,2) + (3,4)  # (1,2,3,4)" },
        { name:"t * n",            cat:"Insert",  desc:"Repetition returns a NEW tuple",                                    code:"(0,) * 5  # (0,0,0,0,0)" },
    ],
    questions:[
        {
            title:"Use tuples as dictionary keys (coordinate map)",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 Tuple keys in dict", tip:"Tuples are hashable → can be dict keys. Lists cannot (unhashable).",
                    code:`# Tuples are hashable — can be used as dict keys!
grid = {}
grid[(0, 0)] = "start"
grid[(1, 2)] = "treasure"
grid[(3, 3)] = "end"

print(grid[(1, 2)])  # "treasure"

# Common use: memoization cache keyed by arguments
cache = {}
def fib(n):
    if n <= 1: return n
    if (n,) in cache: return cache[(n,)]
    cache[(n,)] = fib(n-1) + fib(n-2)
    return cache[(n,)]` },
                { label:"🟠 namedtuple for readable records", tip:"Named fields prevent index confusion — reads like a dataclass.",
                    code:`from collections import namedtuple

# Define a record type once
Student = namedtuple('Student', ['name', 'grade', 'score'])

students = [
    Student('Alice', 'A', 95),
    Student('Bob',   'B', 82),
    Student('Carol', 'A', 91),
]

# Access by name — much clearer than students[0][2]
for s in students:
    print(f"{s.name}: {s.score}")

# Sort by score descending
top = sorted(students, key=lambda s: s.score, reverse=True)
print(top[0].name)  # Alice` },
                { label:"🟢 Tuple unpacking in loops", tip:"Unpack tuples directly in for-loop — avoids index access.",
                    code:`# Tuple unpacking makes multi-value iterations clean
coordinates = [(1,2), (3,4), (5,6)]

for x, y in coordinates:
    print(f"({x}, {y})")

# Swap variables with tuple packing/unpacking
a, b = 10, 20
a, b = b, a   # Pythonic swap — no temp variable needed!
print(a, b)   # 20 10

# Extended unpacking
first, *middle, last = (1, 2, 3, 4, 5)
print(first, last)  # 1 5` },
            ],
        },
        {
            title:"Return multiple values and unpack results",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 Return tuple from function", tip:"Python functions return tuples implicitly — no explicit tuple() needed.",
                    code:`def min_max(numbers):
    # Returning two values automatically creates a tuple
    return min(numbers), max(numbers)

lo, hi = min_max([3, 1, 4, 1, 5, 9, 2, 6])
print(f"Min: {lo}, Max: {hi}")  # Min: 1, Max: 9

# The actual return value is a tuple:
result = min_max([3,1,4])
print(type(result))  # <class 'tuple'>
print(result)        # (1, 4)` },
                { label:"🟠 divmod and enumerate", tip:"Built-in functions like divmod() and enumerate() return tuples.",
                    code:`# divmod() returns (quotient, remainder) as a tuple
quotient, remainder = divmod(17, 5)
print(quotient, remainder)  # 3 2

# enumerate() returns (index, value) tuples
fruits = ['apple', 'banana', 'cherry']
for i, fruit in enumerate(fruits, start=1):
    print(f"{i}. {fruit}")
# 1. apple  2. banana  3. cherry

# zip() combines iterables into tuples
names   = ['Alice', 'Bob']
scores  = [95, 82]
for name, score in zip(names, scores):
    print(f"{name}: {score}")` },
                { label:"🟢 Named tuple as lightweight dataclass", tip:"namedtuple adds field names to tuples — still immutable and fast.",
                    code:`from collections import namedtuple

# Define the structure once — like a mini-class
Point3D = namedtuple('Point3D', ['x', 'y', 'z'])

def calculate_midpoint(p1, p2):
    return Point3D(
        x=(p1.x + p2.x) / 2,
        y=(p1.y + p2.y) / 2,
        z=(p1.z + p2.z) / 2,
    )

a = Point3D(0, 0, 0)
b = Point3D(4, 6, 8)
mid = calculate_midpoint(a, b)
print(mid)      # Point3D(x=2.0, y=3.0, z=4.0)
print(mid.x)    # 2.0  (named access)
print(mid[0])   # 2.0  (index access — still works!)` },
            ],
        },
        {
            title:"Tuple vs list performance comparison",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 Immutability as a feature", tip:"Use tuple to signal data should not change — self-documenting code.",
                    code:`# Use tuple when data should NEVER change
DIRECTIONS = ('N', 'S', 'E', 'W')  # constant
RGB_RED     = (255, 0, 0)           # colour constant
DB_CONFIG   = ('localhost', 5432, 'mydb')

# Tuples are hashable → usable as set elements
visited_positions = set()
visited_positions.add((0, 0))
visited_positions.add((1, 2))
visited_positions.add((0, 0))   # duplicate — ignored
print(len(visited_positions))   # 2

# Lists are NOT hashable:
# {[0,0]}  → TypeError: unhashable type: 'list'` },
                { label:"🟠 When to convert tuple ↔ list", tip:"Convert to list to modify, back to tuple when done.",
                    code:`# Read-only data arrives as tuple
config = ('host', 3000, 'debug')

# Need to update? Convert to list, change, convert back
cfg_list = list(config)
cfg_list[1] = 8080        # update port
config = tuple(cfg_list)  # back to immutable
print(config)             # ('host', 8080, 'debug')

# Sorting tuples: sorted() works (returns list)
scores = [(3,'C'), (1,'A'), (2,'B')]
scores.sort()           # sorts by first element
print(scores)           # [(1,'A'),(2,'B'),(3,'C')]` },
                { label:"🟢 Packing / unpacking patterns", tip:"Star unpacking is powerful — captures remaining elements into a list.",
                    code:`# Star unpacking (*) — captures remaining into a list
first, *rest = [1, 2, 3, 4, 5]
print(first)  # 1
print(rest)   # [2, 3, 4, 5]

*head, last = [1, 2, 3, 4, 5]
print(head)   # [1, 2, 3, 4]
print(last)   # 5

first, *middle, last = [1, 2, 3, 4, 5]
print(middle) # [2, 3, 4]

# Useful for processing variable-length data:
def process(first_item, *remaining):
    print(f"First: {first_item}")
    print(f"Rest: {remaining}")

process(1, 2, 3, 4)
# First: 1  |  Rest: (2, 3, 4)` },
            ],
        },
    ],
};
// ── set ───────────────────────────────────────────────────────
const SET_DATA = {
    icon:"🔷", color:"#58a6ff",
    tagline:"Unordered, unique elements — O(1) membership test",
    badge:"set (built-in)",
    whenToUse:"Remove duplicates; fast membership test; set algebra (union, intersection, difference)",
    declare:"my_set = set()  # NOT {} which creates empty dict!",
    complexity:{ add:"O(1)", remove:"O(1)", in:"O(1)", iterate:"O(n)" },
    note:"set is mutable and unhashable; frozenset is immutable and hashable (usable as dict key)",
    methods:[
        { name:"add(x)",              cat:"Insert",  desc:"Adds x — silently ignores if already present. O(1)",                    code:"s = {1, 2}\ns.add(3)\ns.add(2)  # ignored — already in set\n# {1, 2, 3}" },
        { name:"update(*iterables)",  cat:"Insert",  desc:"Adds all elements from one or more iterables — like |= but multi-arg",  code:"s.update([4, 5], [6])\n# adds 4, 5, 6" },
        { name:"remove(x)",           cat:"Delete",  desc:"Removes x — raises KeyError if not present. Use discard() to be safe",  code:"s.remove(3)   # KeyError if not found\ns.discard(99)  # safe — no error if missing" },
        { name:"discard(x)",          cat:"Delete",  desc:"Removes x if present — does NOT raise error if missing. Prefer this",   code:"s.discard(999)  # silently does nothing" },
        { name:"pop()",               cat:"Delete",  desc:"Removes and returns an ARBITRARY element — order not guaranteed",        code:"x = s.pop()  # removes some element" },
        { name:"clear()",             cat:"Delete",  desc:"Removes all elements from the set",                                     code:"s.clear()  # set()" },
        { name:"in operator",         cat:"Info",    desc:"O(1) hash-based membership test — much faster than list's O(n)",        code:"3 in s    # True\n9 not in s  # True" },
        { name:"len(s)",              cat:"Info",    desc:"Number of unique elements",                                              code:"len({1, 2, 3})  # 3" },
        { name:"union(| or method)",  cat:"Modify",  desc:"Returns NEW set with all elements from both sets",                      code:"a | b           # operator\na.union(b)       # method — same result\na.union(b, c, d) # method can take multiple" },
        { name:"intersection(&)",     cat:"Modify",  desc:"Returns NEW set with only elements in BOTH sets",                      code:"a & b\na.intersection(b)" },
        { name:"difference(-)",       cat:"Modify",  desc:"Returns NEW set with elements in a but NOT in b",                      code:"a - b\na.difference(b)" },
        { name:"symmetric_difference",cat:"Modify",  desc:"Elements in EITHER a or b but NOT BOTH",                               code:"a ^ b\na.symmetric_difference(b)" },
        { name:"issubset(other)",     cat:"Info",    desc:"True if every element of self is in other (self ⊆ other)",              code:"a.issubset(b)  # or  a <= b" },
        { name:"issuperset(other)",   cat:"Info",    desc:"True if self contains every element of other (self ⊇ other)",           code:"a.issuperset(b)  # or  a >= b" },
        { name:"isdisjoint(other)",   cat:"Info",    desc:"True if a and b share NO elements",                                    code:"a.isdisjoint(b)  # True if a ∩ b = ∅" },
    ],
    questions:[
        {
            title:"Set operations: union, intersection, difference",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 Operator syntax", tip:"Python set operators are the most Pythonic — use these first.",
                    code:`a = {1, 2, 3, 4, 5}
b = {3, 4, 5, 6, 7}

print(a | b)   # Union       → {1,2,3,4,5,6,7}
print(a & b)   # Intersection → {3,4,5}
print(a - b)   # Difference  → {1,2}
print(a ^ b)   # Sym diff    → {1,2,6,7}

# Update in-place (modify a):
a |= b   # a = a ∪ b
a &= b   # a = a ∩ b
a -= b   # a = a − b` },
                { label:"🟠 Method syntax (multi-set support)", tip:"Methods can take multiple arguments — operators cannot.",
                    code:`a = {1, 2, 3}
b = {2, 3, 4}
c = {3, 4, 5}

# Methods support multiple sets — operators don't!
union3     = a.union(b, c)        # {1,2,3,4,5}
inter3     = a.intersection(b, c) # {3}
diff_multi = a.difference(b, c)   # {1}

print(union3)   # {1, 2, 3, 4, 5}
print(inter3)   # {3}` },
                { label:"🟢 Set comprehension", tip:"Build a set with conditions in one expression — like list comprehension.",
                    code:`nums = [1, 2, 3, 2, 4, 3, 5, 1]

# Set comprehension removes duplicates automatically
unique_evens = {x for x in nums if x % 2 == 0}
print(unique_evens)  # {2, 4}

# Useful: find characters unique to one string
s1 = set("python")
s2 = set("typhoon")
only_in_s1 = s1 - s2
print(only_in_s1)  # {'p'}  (in python but not typhoon)` },
            ],
        },
        {
            title:"Remove duplicates while preserving order",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 dict.fromkeys() (preserves order)", tip:"dict.fromkeys preserves insertion order since Python 3.7.",
                    code:`nums = [1, 2, 2, 3, 4, 3, 5, 1]

# dict.fromkeys preserves order AND removes dups
unique = list(dict.fromkeys(nums))
print(unique)  # [1, 2, 3, 4, 5]  ← order preserved!

# Note: set() does NOT preserve order:
print(list(set(nums)))  # [1,2,3,4,5] but order varies` },
                { label:"🟠 Seen set + loop", tip:"Explicit loop with seen set — easy to understand and extend.",
                    code:`nums = [1, 2, 2, 3, 4, 3, 5, 1]
seen = set()
unique = []

for n in nums:
    if n not in seen:   # O(1) lookup
        unique.append(n)
        seen.add(n)

print(unique)  # [1, 2, 3, 4, 5]` },
                { label:"🟢 OrderedDict approach", tip:"Older Python pattern — dict.fromkeys() is preferred in modern Python.",
                    code:`from collections import OrderedDict

nums = [1, 2, 2, 3, 4, 3, 5, 1]

# OrderedDict remembers insertion order
unique = list(OrderedDict.fromkeys(nums))
print(unique)  # [1, 2, 3, 4, 5]

# In Python 3.7+, regular dict also preserves order
# so dict.fromkeys() is the modern preference` },
            ],
        },
        {
            title:"Find common elements in multiple lists",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 Set intersection chain", tip:"Convert first list to set, then intersect with each remaining.",
                    code:`def common_elements(*lists):
    if not lists:
        return set()
    # Start with first list as a set
    result = set(lists[0])
    # Intersect with each subsequent list
    for lst in lists[1:]:
        result &= set(lst)
    return result

a = [1, 2, 3, 4, 5]
b = [3, 4, 5, 6, 7]
c = [4, 5, 6, 8, 9]

print(common_elements(a, b, c))  # {4, 5}` },
                { label:"🟠 set.intersection(*args)", tip:"Pass all sets at once — cleaner than chaining &= in a loop.",
                    code:`lists = [[1,2,3,4], [2,3,4,5], [3,4,5,6]]

# Convert all to sets and unpack into intersection()
common = set(lists[0]).intersection(*lists[1:])
print(common)  # {3, 4}` },
                { label:"🟢 Counter-based (find exactly n-common)", tip:"Counter approach lets you find elements common to exactly k lists.",
                    code:`from collections import Counter

lists = [[1,2,3], [2,3,4], [3,4,5]]

# Count how many lists each element appears in
element_count = Counter()
for lst in lists:
    element_count.update(set(lst))  # set prevents counting dups within same list

# Elements in ALL 3 lists
in_all = [k for k, v in element_count.items() if v == len(lists)]
print(in_all)  # [3]

# Elements in at least 2 lists
in_two_plus = [k for k, v in element_count.items() if v >= 2]
print(in_two_plus)  # [2, 3, 4]` },
            ],
        },
    ],
};

// ── frozenset ─────────────────────────────────────────────────
const FROZENSET_DATA = {
    icon:"❄️", color:"#a5f3fc",
    tagline:"Immutable, hashable set — usable as dict keys and set elements",
    badge:"frozenset (built-in)",
    whenToUse:"Immutable set constants; hashable set for dict keys; cache keys in memoization",
    declare:"fs = frozenset([1, 2, 3])  # or  frozenset({1, 2, 3})",
    complexity:{ in:"O(1)", len:"O(1)", iterate:"O(n)", frozenset:"O(n)" },
    note:"frozenset supports all non-mutating set operations but NOT add/remove/update",
    methods:[
        { name:"in operator",         cat:"Info",    desc:"O(1) membership test — same speed as set",                            code:"fs = frozenset([1,2,3])\n1 in fs   # True" },
        { name:"len(fs)",             cat:"Info",    desc:"Number of elements",                                                  code:"len(frozenset([1,2,3]))  # 3" },
        { name:"union(| or method)",  cat:"Info",    desc:"Returns a NEW frozenset union — does not mutate",                    code:"a | b\na.union(b)" },
        { name:"intersection(&)",     cat:"Info",    desc:"Returns a NEW frozenset intersection",                                code:"a & b\na.intersection(b)" },
        { name:"difference(-)",       cat:"Info",    desc:"Returns a NEW frozenset difference",                                  code:"a - b\na.difference(b)" },
        { name:"issubset(other)",     cat:"Info",    desc:"True if every element of self is in other",                           code:"a.issubset(b)  # or  a <= b" },
        { name:"issuperset(other)",   cat:"Info",    desc:"True if self contains every element of other",                        code:"a.issuperset(b)  # or  a >= b" },
        { name:"isdisjoint(other)",   cat:"Info",    desc:"True if no elements shared",                                          code:"a.isdisjoint(b)" },
        { name:"frozenset(iterable)", cat:"Convert", desc:"Creates frozenset from any iterable",                                 code:"frozenset([1,2,3])\nfrozenset('abc')  # frozenset({'a','b','c'})\nfrozenset(range(5))" },
        { name:"copy()",              cat:"Convert", desc:"Returns a shallow copy (also a frozenset)",                           code:"fs2 = fs.copy()" },
    ],
    questions:[
        {
            title:"Use frozenset as a dictionary key",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 Graph edge deduplication", tip:"frozenset({A,B}) == frozenset({B,A}) — order-independent edge key.",
                    code:`# frozenset is hashable → can be dict key
# Perfect for undirected graph edges (order doesn't matter)
edge_weights = {}
edge_weights[frozenset({'A', 'B'})] = 5
edge_weights[frozenset({'B', 'C'})] = 3
edge_weights[frozenset({'A', 'C'})] = 7

# Look up edge — order doesn't matter!
print(edge_weights[frozenset({'B', 'A'})])  # 5  (same as A,B)
print(edge_weights[frozenset({'C', 'B'})])  # 3` },
                { label:"🟠 Memoize with set arguments", tip:"Regular set is unhashable — frozenset lets you cache set-based calls.",
                    code:`from functools import lru_cache

# Can't use lru_cache with set args — use frozenset!
@lru_cache(maxsize=None)
def count_paths(remaining_nodes: frozenset, current: str) -> int:
    if not remaining_nodes:
        return 1
    return sum(
        count_paths(remaining_nodes - {next_n}, next_n)
        for next_n in remaining_nodes
    )

nodes = frozenset({'A', 'B', 'C'})
print(count_paths(nodes, 'start'))  # all permutations` },
                { label:"🟢 frozenset vs set when to choose", tip:"Simple rule: mutable data → set; hashable/constant → frozenset.",
                    code:`# Use frozenset when:
# 1. Need to use a set as a dict key or set element
# 2. Want to guarantee data won't change
# 3. Sharing a set safely across functions

VOWELS = frozenset('aeiou')  # constant — won't change
def is_vowel(c):
    return c.lower() in VOWELS  # O(1) lookup

# frozenset CAN be an element of a regular set:
sets_of_sets = {frozenset([1,2]), frozenset([3,4])}
print(sets_of_sets)

# Regular set CANNOT be an element:
# {set([1,2])}  → TypeError: unhashable type: 'set'` },
            ],
        },
        {
            title:"frozenset for graph algorithms",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 Visited states in BFS (with sets)", tip:"When visited state IS a set, frozenset makes it hashable.",
                    code:`from collections import deque

def word_ladder(start, end, word_list):
    """Find shortest transformation sequence."""
    words = set(word_list)
    queue = deque([(start, [start])])
    visited = {start}

    while queue:
        word, path = queue.popleft()
        if word == end:
            return path

        for i in range(len(word)):
            for c in 'abcdefghijklmnopqrstuvwxyz':
                candidate = word[:i] + c + word[i+1:]
                if candidate in words and candidate not in visited:
                    visited.add(candidate)
                    queue.append((candidate, path + [candidate]))
    return []

print(word_ladder("hit", "cog", ["hot","dot","dog","lot","log","cog"]))
# ['hit', 'hot', 'dot', 'dog', 'cog']` },
                { label:"🟠 Finding cliques (frozenset as key)", tip:"frozenset lets you store unordered groups without duplicating A-B vs B-A.",
                    code:`def find_pairs_with_sum(nums, target):
    """Find all unique pairs that sum to target."""
    seen = set()
    pairs = set()  # store pairs as frozensets

    for n in nums:
        complement = target - n
        if complement in seen:
            # frozenset ensures {2,5} == {5,2}
            pairs.add(frozenset({n, complement}))
        seen.add(n)

    return [tuple(p) for p in pairs]

print(find_pairs_with_sum([1,2,3,4,5,6], 7))
# [(1,6), (2,5), (3,4)] — no duplicates!` },
                { label:"🟢 Symmetric encryption key sets", tip:"frozenset represents a set of required permissions/capabilities as a hashable key.",
                    code:`# Permission sets as frozenset — cacheable and hashable
PERMISSIONS = {
    frozenset({'read'}):             'basic_user',
    frozenset({'read', 'write'}):    'editor',
    frozenset({'read','write','admin'}): 'admin',
}

def get_role(user_permissions: set) -> str:
    # Convert to frozenset for lookup
    key = frozenset(user_permissions)
    return PERMISSIONS.get(key, 'unknown')

print(get_role({'read', 'write'}))   # 'editor'
print(get_role({'write', 'read'}))   # 'editor' (order irrelevant!)` },
            ],
        },
        {
            title:"frozenset in collections (as set element)",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 Set of frozensets", tip:"Regular sets cannot contain sets — frozensets solve this.",
                    code:`# Store all unique COMBINATIONS of items
all_combos = set()
items = [1, 2, 3, 4]

# Generate all pairs
for i in range(len(items)):
    for j in range(i+1, len(items)):
        combo = frozenset({items[i], items[j]})
        all_combos.add(combo)  # works! frozenset is hashable

print(len(all_combos))  # 6 unique pairs
print(all_combos)` },
                { label:"🟠 Anagram grouping", tip:"Sorted letters as frozenset key groups anagrams together.",
                    code:`from collections import defaultdict

words = ['eat', 'tea', 'tan', 'ate', 'nat', 'bat']

groups = defaultdict(list)
for word in words:
    # frozenset of chars groups anagrams (ignores order & freq)
    # For exact anagram: use tuple(sorted(word)) instead
    key = tuple(sorted(word))   # ('a','e','t') for eat/tea/ate
    groups[key].append(word)

print(list(groups.values()))
# [['eat','tea','ate'], ['tan','nat'], ['bat']]` },
                { label:"🟢 Deduplicate list of lists", tip:"Lists can't go in a set — convert to frozensets first.",
                    code:`# Problem: remove duplicate lists from a list of lists
data = [[1,2,3], [3,2,1], [1,2,3], [4,5,6], [4,5,6]]

# Option 1: frozenset (order-insensitive dedup)
unique_order_insensitive = list({frozenset(x) for x in data})
print(len(unique_order_insensitive))  # 3: {1,2,3}, {3,2,1}=same, {4,5,6}

# Option 2: tuple (order-sensitive dedup)
unique_order_sensitive = list({tuple(x) for x in data})
print(len(unique_order_sensitive))  # 3: [1,2,3],[3,2,1] are different` },
            ],
        },
    ],
};
// ── dict ──────────────────────────────────────────────────────
const DICT_DATA = {
    icon:"🗺️", color:"#7ee787",
    tagline:"Key→Value pairs — O(1) lookup, insertion-ordered since Python 3.7",
    badge:"dict (built-in)",
    whenToUse:"Key-value mappings; frequency counting; memoization; grouping data",
    declare:"my_dict = {}  # or  my_dict = dict()",
    complexity:{ get:"O(1)", set:"O(1)", delete:"O(1)", search:"O(n)" },
    note:"Keys must be hashable (strings, numbers, tuples). Insertion order preserved since Python 3.7",
    methods:[
        { name:"d[key] = value",       cat:"Insert",  desc:"Set or update a key-value pair — O(1)",                               code:"d = {}\nd['name'] = 'Alice'\nd['age']  = 30" },
        { name:"d.update(other)",      cat:"Insert",  desc:"Merges another dict (or key=value pairs) into d — overwrites dups",   code:"d.update({'city':'London'})\nd.update(role='admin')  # keyword args ok too" },
        { name:"d.setdefault(k, v)",   cat:"Insert",  desc:"Inserts key with default ONLY if key missing — returns value",        code:"d.setdefault('score', 0)   # inserts 0 if 'score' absent\nd.setdefault('name', 'X') # 'Alice' — key exists, no change" },
        { name:"d[key]",               cat:"Access",  desc:"Get value — raises KeyError if key missing. Use .get() to be safe",   code:"d['name']          # 'Alice'\nd['missing']       # KeyError!" },
        { name:"d.get(key, default)",  cat:"Access",  desc:"Returns value or default (None if not given) — never raises error",   code:"d.get('name')        # 'Alice'\nd.get('x', 'N/A')   # 'N/A' — safe!" },
        { name:"d.pop(key, default)",  cat:"Delete",  desc:"Removes and returns value — raises KeyError if missing and no default",code:"d.pop('age')      # removes and returns 30\nd.pop('x', None)  # safe delete" },
        { name:"del d[key]",           cat:"Delete",  desc:"Deletes key — raises KeyError if not present",                        code:"del d['name']  # raises KeyError if absent" },
        { name:"d.clear()",            cat:"Delete",  desc:"Removes ALL key-value pairs",                                         code:"d.clear()  # {}" },
        { name:"key in d",             cat:"Info",    desc:"O(1) key membership test — checks keys, NOT values",                  code:"'name' in d     # True\n'name' not in d  # False" },
        { name:"len(d)",               cat:"Info",    desc:"Returns number of key-value pairs",                                   code:"len({'a':1,'b':2})  # 2" },
        { name:"d.keys()",             cat:"Iterate", desc:"Returns dict_keys view — reflects changes to dict in real time",      code:"for k in d.keys(): print(k)\nlist(d.keys())  # ['name','age',...]" },
        { name:"d.values()",           cat:"Iterate", desc:"Returns dict_values view of all values",                              code:"for v in d.values(): print(v)" },
        { name:"d.items()",            cat:"Iterate", desc:"Returns dict_items view of (key,value) tuples — most common pattern",code:"for k, v in d.items():\n    print(k, v)" },
        { name:"dict comprehension",   cat:"Iterate", desc:"Build a dict from any iterable in one expression",                   code:"squares = {x: x**2 for x in range(5)}\n# {0:0, 1:1, 2:4, 3:9, 4:16}" },
        { name:"{**d1, **d2}",         cat:"Modify",  desc:"Merge dicts — later keys overwrite earlier ones (Python 3.9+: d1|d2)", code:"merged = {**d1, **d2}\n# Python 3.9+:\nmerged = d1 | d2" },
        { name:"d.copy()",             cat:"Convert", desc:"Shallow copy — nested objects are still shared references",            code:"d2 = d.copy()   # shallow\nimport copy; d3 = copy.deepcopy(d)  # deep" },
    ],
    questions:[
        {
            title:"Count word frequency with a dict",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 dict.get() pattern", tip:"get(w, 0) + 1 handles missing keys cleanly — no KeyError risk.",
                    code:`words = ['apple','banana','apple','cherry','banana','apple']

freq = {}
for word in words:
    # get returns 0 if key absent — then increment
    freq[word] = freq.get(word, 0) + 1

# Sort by frequency descending
for word, count in sorted(freq.items(), key=lambda x: -x[1]):
    print(f"{word}: {count}")
# apple: 3 | banana: 2 | cherry: 1` },
                { label:"🟠 collections.Counter", tip:"Counter is a dict subclass purpose-built for counting.",
                    code:`from collections import Counter

words = ['apple','banana','apple','cherry','banana','apple']
freq = Counter(words)

print(freq)                    # Counter({'apple':3,'banana':2,'cherry':1})
print(freq['apple'])           # 3
print(freq.most_common(2))     # [('apple',3),('banana',2)]
print(freq.most_common()[:-3:-1])  # 2 least common` },
                { label:"🟢 defaultdict(int)", tip:"defaultdict auto-creates missing keys — no get() needed at all.",
                    code:`from collections import defaultdict

words = ['apple','banana','apple','cherry']
# int default is 0 — missing keys automatically get 0
freq = defaultdict(int)
for word in words:
    freq[word] += 1  # no KeyError — missing key → 0 automatically!

print(dict(freq))  # {'apple':2,'banana':1,'cherry':1}` },
            ],
        },
        {
            title:"Group items by a property",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 Manual dict grouping", tip:"setdefault creates the list on first access — clean pattern.",
                    code:`people = [
    {'name':'Alice','dept':'Eng'},
    {'name':'Bob',  'dept':'HR'},
    {'name':'Carol','dept':'Eng'},
]

groups = {}
for p in people:
    # setdefault creates [] only if key absent
    groups.setdefault(p['dept'], []).append(p['name'])

print(groups)
# {'Eng': ['Alice', 'Carol'], 'HR': ['Bob']}` },
                { label:"🟠 defaultdict(list)", tip:"defaultdict(list) auto-creates empty list — most Pythonic pattern.",
                    code:`from collections import defaultdict

groups = defaultdict(list)
for p in people:
    groups[p['dept']].append(p['name'])  # no setdefault needed!

print(dict(groups))  # {'Eng':['Alice','Carol'],'HR':['Bob']}` },
                { label:"🟢 itertools.groupby", tip:"groupby requires sorted input — pairs well with sorted() key.",
                    code:`from itertools import groupby

# MUST sort by the same key before groupby
people_sorted = sorted(people, key=lambda p: p['dept'])

groups = {
    dept: [p['name'] for p in members]
    for dept, members in groupby(people_sorted, key=lambda p: p['dept'])
}
print(groups)  # {'Eng':['Alice','Carol'],'HR':['Bob']}` },
            ],
        },
        {
            title:"Invert a dictionary (swap keys and values)",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 Dict comprehension", tip:"Swap k,v in one expression. Assumes values are unique and hashable.",
                    code:`original = {'a': 1, 'b': 2, 'c': 3}

inverted = {v: k for k, v in original.items()}
print(inverted)  # {1:'a', 2:'b', 3:'c'}

# Handle non-unique values (group keys by value):
from collections import defaultdict
multi = defaultdict(list)
for k, v in original.items():
    multi[v].append(k)` },
                { label:"🟠 zip approach", tip:"zip(values, keys) pairs them in reverse order.",
                    code:`original = {'a': 1, 'b': 2, 'c': 3}

# zip values with keys — swap the order
inverted = dict(zip(original.values(), original.keys()))
print(inverted)  # {1:'a', 2:'b', 3:'c'}` },
                { label:"🟢 map and lambda", tip:"map(reversed, items()) then dict() — functional style.",
                    code:`original = {'a':1, 'b':2, 'c':3}
inverted = dict(map(lambda item: (item[1], item[0]), original.items()))
print(inverted)  # {1:'a',2:'b',3:'c'}` },
            ],
        },
    ],
};

// ── collections.deque ─────────────────────────────────────────
const DEQUE_DATA = {
    icon:"🔗", color:"#e879f9",
    tagline:"Double-ended queue — O(1) append/pop from BOTH ends",
    badge:"collections.deque",
    whenToUse:"Queue; Stack; sliding window; BFS; history buffer with maxlen; rotating sequences",
    declare:"from collections import deque\ndq = deque()",
    complexity:{ appendleft:"O(1)", popleft:"O(1)", append:"O(1)", pop:"O(1)" },
    note:"list.pop(0) is O(n); deque.popleft() is O(1) — always use deque for queue operations!",
    methods:[
        { name:"append(x)",       cat:"Insert",  desc:"Adds x to the RIGHT end — O(1)",                                        code:"dq = deque([1,2,3])\ndq.append(4)\n# deque([1,2,3,4])" },
        { name:"appendleft(x)",   cat:"Insert",  desc:"Adds x to the LEFT end — O(1) (impossible efficiently with list)",       code:"dq.appendleft(0)\n# deque([0,1,2,3,4])" },
        { name:"extend(iter)",    cat:"Insert",  desc:"Extends right end from iterable",                                        code:"dq.extend([5,6])" },
        { name:"extendleft(iter)",cat:"Insert",  desc:"Extends left end — elements are added one-by-one, REVERSED order",       code:"dq.extendleft([7,8])\n# adds 8 then 7 to left" },
        { name:"pop()",           cat:"Delete",  desc:"Removes and returns rightmost element — O(1)",                           code:"dq.pop()  # removes right" },
        { name:"popleft()",       cat:"Delete",  desc:"Removes and returns leftmost element — O(1). Use for Queue dequeue",     code:"dq.popleft()  # removes left — USE THIS not list.pop(0)!" },
        { name:"rotate(n)",       cat:"Modify",  desc:"Rotates right by n (negative = left). Wraps around efficiently",         code:"dq.rotate(1)   # rotate right\ndq.rotate(-1)  # rotate left" },
        { name:"maxlen",          cat:"Info",    desc:"Optional max size — oldest elements auto-dropped when full",              code:"history = deque(maxlen=5)\n# auto-removes oldest when > 5 items" },
        { name:"dq[i]",           cat:"Access",  desc:"Index access — O(1) at ends, O(n) in middle",                           code:"dq[0]   # leftmost (O(1))\ndq[-1]  # rightmost (O(1))\ndq[2]   # middle (O(n))" },
        { name:"len(dq)",         cat:"Info",    desc:"Number of elements — O(1)",                                              code:"len(dq)  # count" },
        { name:"count(x)",        cat:"Info",    desc:"Number of occurrences of x — O(n)",                                     code:"dq.count(3)" },
        { name:"clear()",         cat:"Delete",  desc:"Removes all elements",                                                   code:"dq.clear()" },
        { name:"copy()",          cat:"Convert", desc:"Returns a shallow copy of the deque",                                    code:"dq2 = dq.copy()" },
        { name:"list(dq)",        cat:"Convert", desc:"Converts deque to list",                                                 code:"list(dq)" },
    ],
    questions:[
        {
            title:"BFS shortest path using deque",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 Standard BFS with deque", tip:"deque.popleft() is O(1) — essential for correct BFS time complexity.",
                    code:`from collections import deque

def bfs_shortest_path(graph, start, end):
    if start == end:
        return [start]

    queue   = deque([(start, [start])])  # (node, path)
    visited = {start}

    while queue:
        node, path = queue.popleft()  # O(1) — crucial!

        for neighbour in graph.get(node, []):
            if neighbour == end:
                return path + [neighbour]
            if neighbour not in visited:
                visited.add(neighbour)
                queue.append((neighbour, path + [neighbour]))

    return None  # no path found

graph = {'A':['B','C'],'B':['D'],'C':['D'],'D':['E'],'E':[]}
print(bfs_shortest_path(graph,'A','E'))
# ['A','B','D','E'] or ['A','C','D','E']` },
                { label:"🟠 Level-order tree traversal", tip:"Use queue size snapshot to process exactly one level per iteration.",
                    code:`from collections import deque

def level_order(root):
    if not root:
        return []
    queue = deque([root])
    levels = []

    while queue:
        level_size = len(queue)  # snapshot — this level's node count
        level = []

        for _ in range(level_size):
            node = queue.popleft()
            level.append(node.val)
            if node.left:  queue.append(node.left)
            if node.right: queue.append(node.right)

        levels.append(level)

    return levels
# Returns [[root], [level1], [level2], ...]` },
                { label:"🟢 Sliding window maximum (monotonic deque)", tip:"Store indices in decreasing order. Front = max of current window. O(n).",
                    code:`from collections import deque

def max_sliding_window(nums, k):
    dq = deque()   # stores indices, kept in decreasing value order
    result = []

    for i, num in enumerate(nums):
        # Remove indices outside current window [i-k+1, i]
        while dq and dq[0] < i - k + 1:
            dq.popleft()

        # Remove indices whose values are smaller than current
        # (they can never be the max of any future window)
        while dq and nums[dq[-1]] < num:
            dq.pop()

        dq.append(i)
        if i >= k - 1:  # window is full
            result.append(nums[dq[0]])  # front = max

    return result

print(max_sliding_window([1,3,-1,-3,5,3,6,7], 3))
# [3, 3, 5, 5, 6, 7]` },
            ],
        },
        {
            title:"Use deque as Stack and Queue",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 Queue (FIFO) with deque", tip:"append right, popleft — correct O(1) FIFO. Never use list for a Queue.",
                    code:`from collections import deque

# FIFO Queue: enqueue right, dequeue left
queue = deque()
queue.append('Alice')    # enqueue
queue.append('Bob')
queue.append('Charlie')

print(queue.popleft())  # Alice — first in, first out
print(queue.popleft())  # Bob
print(queue)            # deque(['Charlie'])` },
                { label:"🟠 Stack (LIFO) with deque", tip:"append right, pop right — LIFO stack. (list also works for pure stacks.)",
                    code:`from collections import deque

# LIFO Stack: push right, pop right
stack = deque()
stack.append(10)  # push
stack.append(20)
stack.append(30)

print(stack.pop())  # 30 — last in, first out
print(stack.pop())  # 20
print(stack)        # deque([10])` },
                { label:"🟢 Bounded history buffer (maxlen)", tip:"maxlen auto-discards oldest entries — perfect for undo history or log buffers.",
                    code:`from collections import deque

# Last 5 commands — oldest auto-removed when full
history = deque(maxlen=5)

for cmd in ['ls','cd /','pwd','echo hi','cat f','grep x','wc -l']:
    history.append(cmd)
    # Automatically drops oldest when > 5

print(list(history))
# ['cd /','pwd','echo hi','cat f','grep x','wc -l']
# First 'ls' was auto-dropped when 6th command added` },
            ],
        },
        {
            title:"Palindrome check and rotate with deque",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 Palindrome check using popleft/pop", tip:"Pop from both ends simultaneously — cleaner than index juggling.",
                    code:`from collections import deque

def is_palindrome(s):
    dq = deque(s.lower().replace(' ',''))
    while len(dq) > 1:
        if dq.popleft() != dq.pop():
            return False
    return True

print(is_palindrome("racecar"))      # True
print(is_palindrome("A man a plan a canal Panama"))  # True
print(is_palindrome("hello"))        # False` },
                { label:"🟠 Rotate a sequence", tip:"deque.rotate is O(k) and built-in — cleaner than manual slicing.",
                    code:`from collections import deque

tasks = deque(['A','B','C','D','E'])

# Rotate right by 2 (last 2 move to front)
tasks.rotate(2)
print(list(tasks))  # ['D','E','A','B','C']

# Rotate left by 1
tasks.rotate(-1)
print(list(tasks))  # ['E','A','B','C','D']` },
                { label:"🟢 Implement LRU cache manually", tip:"OrderedDict is cleaner but deque shows the underlying concept.",
                    code:`from collections import deque

class LRUCache:
    def __init__(self, capacity):
        self.cap = capacity
        self.cache = {}   # key → value
        self.order = deque()  # tracks access order

    def get(self, key):
        if key not in self.cache:
            return -1
        self.order.remove(key); self.order.append(key)  # move to right (most recent)
        return self.cache[key]

    def put(self, key, value):
        if key in self.cache:
            self.order.remove(key)
        elif len(self.cache) >= self.cap:
            oldest = self.order.popleft()
            del self.cache[oldest]
        self.cache[key] = value
        self.order.append(key)` },
            ],
        },
    ],
};
// ── collections.defaultdict ───────────────────────────────────
const DEFAULTDICT_DATA = {
    icon:"📂", color:"#34d399",
    tagline:"dict that auto-creates missing keys with a default factory",
    badge:"collections.defaultdict",
    whenToUse:"Grouping; graph adjacency lists; frequency counting; any dict needing auto-init",
    declare:"from collections import defaultdict\nd = defaultdict(list)  # or int, set, str...",
    complexity:{ get:"O(1)", set:"O(1)", default:"O(1)", iterate:"O(n)" },
    note:"defaultdict(int) defaults to 0; defaultdict(list) defaults to []; defaultdict(set) defaults to set()",
    methods:[
        { name:"defaultdict(factory)",  cat:"Insert",  desc:"Creates dict with factory fn called on missing key access",   code:"from collections import defaultdict\nd = defaultdict(int)   # missing → 0\nd = defaultdict(list)  # missing → []\nd = defaultdict(set)   # missing → set()" },
        { name:"d[key]",                cat:"Access",  desc:"Access existing key OR auto-create with factory value",        code:"d = defaultdict(int)\nd['x'] += 1  # no KeyError — creates 'x'=0, then +1" },
        { name:"d.get(key, fallback)",  cat:"Access",  desc:"Same as dict.get — does NOT trigger factory, returns fallback", code:"d.get('missing', 'N/A')  # 'N/A' — factory NOT called" },
        { name:"d.default_factory",     cat:"Info",    desc:"The factory function — can be reassigned or set to None",       code:"d.default_factory  # <class 'int'>\nd.default_factory = list  # change factory" },
        { name:"dict(d)",               cat:"Convert", desc:"Converts to regular dict (removes auto-create behaviour)",       code:"regular = dict(d)" },
        { name:"d.items()",             cat:"Iterate", desc:"Same as dict.items() — (key,value) pairs in insertion order",   code:"for k, v in d.items(): print(k, v)" },
    ],
    questions:[
        {
            title:"Build an adjacency list graph",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 defaultdict(list) for graph", tip:"Each missing node auto-gets an empty neighbour list — no init needed.",
                    code:`from collections import defaultdict

graph = defaultdict(list)

edges = [('A','B'),('A','C'),('B','D'),('C','D'),('D','E')]
for u, v in edges:
    graph[u].append(v)  # no KeyError — auto-creates list
    graph[v].append(u)  # undirected: add both directions

print(dict(graph))
# {'A':['B','C'], 'B':['A','D'], 'C':['A','D'],
#  'D':['B','C','E'], 'E':['D']}` },
                { label:"🟠 defaultdict(set) for unique neighbours", tip:"set prevents duplicate edges — use when graph may have duplicate edge input.",
                    code:`from collections import defaultdict

graph = defaultdict(set)
edges = [('A','B'),('A','B'),('A','C'),('B','C')]  # A→B twice

for u, v in edges:
    graph[u].add(v)
    graph[v].add(u)  # undirected

print(dict(graph))
# {'A':{'B','C'}, 'B':{'A','C'}, 'C':{'A','B'}}
# No duplicate A→B!` },
                { label:"🟢 defaultdict(lambda) custom default", tip:"Use lambda for any complex default value — not just built-ins.",
                    code:`from collections import defaultdict

# Default: dict with 'count'=0 and 'items'=[]
node_data = defaultdict(lambda: {'count': 0, 'items': []})

events = [('A','click'),('B','hover'),('A','click'),('A','scroll')]
for node, event in events:
    node_data[node]['count'] += 1
    node_data[node]['items'].append(event)

print(dict(node_data))
# {'A':{'count':3,'items':['click','click','scroll']},
#  'B':{'count':1,'items':['hover']}}` },
            ],
        },
        {
            title:"Nested defaultdict (tree-like structure)",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 Nested defaultdict", tip:"Pass a lambda that returns another defaultdict for infinite nesting.",
                    code:`from collections import defaultdict

# Recursive defaultdict — can nest infinitely deep
def nested():
    return defaultdict(nested)

tree = nested()
tree['animals']['mammals']['cats'] = 'meow'
tree['animals']['mammals']['dogs'] = 'woof'
tree['animals']['birds']['parrot'] = 'squawk'

print(tree['animals']['mammals']['cats'])  # 'meow'
print(tree['animals']['mammals']['dogs'])  # 'woof'` },
                { label:"🟠 Group by multiple keys", tip:"Chain defaultdict(defaultdict(list)) for 2-level grouping.",
                    code:`from collections import defaultdict

employees = [
    {'name':'Alice','dept':'Eng','level':'senior'},
    {'name':'Bob',  'dept':'HR', 'level':'junior'},
    {'name':'Carol','dept':'Eng','level':'junior'},
    {'name':'Dave', 'dept':'Eng','level':'senior'},
]

# Group by dept, then by level
grouped = defaultdict(lambda: defaultdict(list))
for e in employees:
    grouped[e['dept']][e['level']].append(e['name'])

print(grouped['Eng']['senior'])  # ['Alice', 'Dave']
print(grouped['Eng']['junior'])  # ['Carol']` },
                { label:"🟢 Count with defaultdict vs Counter", tip:"Both work for counting. Counter has extra methods like most_common().",
                    code:`from collections import defaultdict, Counter

words = ['the','cat','sat','on','the','mat','the']

# defaultdict approach
freq_dd = defaultdict(int)
for w in words: freq_dd[w] += 1

# Counter approach — same result but more features
freq_c = Counter(words)

# Counter extras:
print(freq_c.most_common(2))  # [('the',3),('cat',1)]
freq_c.update(['cat','cat'])  # add more counts
freq_c.subtract(['the'])      # subtract counts

# They're both dict subclasses — pick based on need` },
            ],
        },
        {
            title:"Word frequency analysis with defaultdict",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 Character frequency map", tip:"Count characters in a string — useful for anagram detection.",
                    code:`from collections import defaultdict

def char_frequency(s):
    freq = defaultdict(int)
    for c in s.lower():
        if c.isalpha():   # ignore spaces/punctuation
            freq[c] += 1
    return freq

def is_anagram(s1, s2):
    return char_frequency(s1) == char_frequency(s2)

print(is_anagram('listen', 'silent'))   # True
print(is_anagram('hello', 'world'))     # False` },
                { label:"🟠 N-gram language model", tip:"defaultdict(defaultdict(int)) builds a 2-gram frequency table.",
                    code:`from collections import defaultdict

def build_bigrams(text):
    words = text.split()
    bigrams = defaultdict(lambda: defaultdict(int))
    for i in range(len(words)-1):
        bigrams[words[i]][words[i+1]] += 1
    return bigrams

text = "the cat sat on the mat the cat sat"
model = build_bigrams(text)
print(dict(model['cat']))    # {'sat': 2}
print(dict(model['the']))    # {'cat': 2, 'mat': 1}` },
                { label:"🟢 Invert index (word → positions)", tip:"Map each word to all positions where it appears.",
                    code:`from collections import defaultdict

def build_index(text):
    index = defaultdict(list)
    for i, word in enumerate(text.lower().split()):
        index[word].append(i)
    return index

text = "the quick brown fox jumps over the lazy dog the"
idx = build_index(text)
print(idx['the'])    # [0, 6, 9] — positions of 'the'
print(idx['fox'])    # [3]` },
            ],
        },
    ],
};

// ── collections.Counter ───────────────────────────────────────
const COUNTER_DATA = {
    icon:"🔢", color:"#fbbf24",
    tagline:"dict subclass for counting hashable objects",
    badge:"collections.Counter",
    whenToUse:"Word frequency; histogram; anagram detection; top-N elements; multiset arithmetic",
    declare:"from collections import Counter\nc = Counter(iterable_or_mapping)",
    complexity:{ count:"O(n)", most_common:"O(n log k)", update:"O(m)", arithmetic:"O(n+m)" },
    note:"Counter is a dict — all dict methods work. Missing keys return 0, not KeyError.",
    methods:[
        { name:"Counter(iterable)",    cat:"Insert",  desc:"Counts each element; can also pass a dict or keyword args",          code:"from collections import Counter\nc = Counter('aabbcc')      # Counter({'a':2,'b':2,'c':2})\nc = Counter([1,1,2,3,3,3])  # Counter({3:3,1:2,2:1})" },
        { name:"c[key]",               cat:"Access",  desc:"Returns count — returns 0 for missing keys (never KeyError)",        code:"c['a']   # 2\nc['z']   # 0 — safe, no error!" },
        { name:"most_common(n)",       cat:"Info",    desc:"Returns n most common as [(elem,count)] sorted descending",           code:"c.most_common(2)   # 2 most frequent\nc.most_common()[-1] # least frequent" },
        { name:"elements()",           cat:"Iterate", desc:"Yields each element repeated count times — positive counts only",    code:"list(Counter('abc').elements())  # ['a','b','c']" },
        { name:"update(iterable)",     cat:"Modify",  desc:"Adds counts (increase). Note: update ADDS, unlike dict.update",      code:"c.update('aab')  # adds counts — NOT replaces" },
        { name:"subtract(iterable)",   cat:"Modify",  desc:"Subtracts counts (can go negative or zero)",                         code:"c.subtract('aab')  # decreases counts" },
        { name:"total()",              cat:"Info",    desc:"Sum of all counts — Python 3.10+ built-in",                          code:"Counter('aab').total()  # 3" },
        { name:"c + d",                cat:"Modify",  desc:"Adds two counters — keeps only positive counts",                     code:"Counter('ab') + Counter('bcc')  # Counter({'b':2,'c':2,'a':1})" },
        { name:"c - d",                cat:"Modify",  desc:"Subtracts counters — drops zeros and negatives",                     code:"Counter('aab') - Counter('ab')  # Counter({'a':1})" },
        { name:"c & d",                cat:"Modify",  desc:"Intersection — takes minimum count for shared elements",             code:"Counter('aab') & Counter('bbc')  # Counter({'b':1})" },
        { name:"c | d",                cat:"Modify",  desc:"Union — takes maximum count for each element",                        code:"Counter('aab') | Counter('bbc')  # Counter({'b':2,'a':2,'c':1})" },
    ],
    questions:[
        {
            title:"Find top-N most frequent words",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 Counter.most_common()", tip:"most_common(n) is O(n log k) — more efficient than sorting all elements.",
                    code:`from collections import Counter
import re

text = """to be or not to be that is the question
whether tis nobler in the mind to suffer"""

# Tokenise and count
words = re.findall(r'\w+', text.lower())
freq = Counter(words)

# Top 5 most common words
print(freq.most_common(5))
# [('to',3),('be',2),('the',2),('or',1),('not',1)]

# Least common:
print(freq.most_common()[:-3:-1])  # last 2` },
                { label:"🟠 Anagram detection", tip:"Counter of both strings must be equal — order is irrelevant.",
                    code:`from collections import Counter

def is_anagram(s1, s2):
    # Counter counts character frequencies
    # Two strings are anagrams if their frequencies match
    return Counter(s1.lower()) == Counter(s2.lower())

def find_anagram_pairs(words):
    groups = {}
    for word in words:
        key = tuple(sorted(word))  # canonical form
        groups.setdefault(key, []).append(word)
    return [g for g in groups.values() if len(g) > 1]

print(is_anagram('listen', 'silent'))   # True
print(find_anagram_pairs(['eat','tea','tan','ate','nat']))
# [['eat','tea','ate'], ['tan','nat']]` },
                { label:"🟢 Counter arithmetic (multiset ops)", tip:"+ adds counts, - subtracts keeping positives, & min, | max.",
                    code:`from collections import Counter

# Inventory management with Counter arithmetic
warehouse = Counter({'apples':50,'bananas':30,'oranges':20})
sold      = Counter({'apples':15,'bananas':5,'pears':10})
restocked = Counter({'apples':10,'oranges':15,'pears':20})

# After selling:
current = warehouse - sold
print(current)  # Counter({'apples':35,'oranges':20,'bananas':25})

# After restocking:
current += restocked
print(current['pears'])   # 10 (0-10=0 after sell, +20 = 20? sold pears not in warehouse)
print(current['apples'])  # 45` },
            ],
        },
        {
            title:"Majority element and k-frequent items",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 Majority element (Counter)", tip:"The majority element appears > n/2 times — check most_common(1).",
                    code:`from collections import Counter

def majority_element(nums):
    # Element appearing more than n/2 times
    count = Counter(nums)
    n = len(nums)
    for elem, freq in count.most_common():
        if freq > n // 2:
            return elem
    return None

print(majority_element([3,2,3]))         # 3
print(majority_element([2,2,1,1,1,2,2])) # 2` },
                { label:"🟠 Top k frequent elements", tip:"most_common(k) is purpose-built for this — cleaner than sorting.",
                    code:`from collections import Counter

def top_k_frequent(nums, k):
    return [elem for elem, _ in Counter(nums).most_common(k)]

print(top_k_frequent([1,1,1,2,2,3], 2))  # [1, 2]
print(top_k_frequent([1], 1))            # [1]

# Bonus: sort by frequency then alphabetically for ties
def top_k_sorted(words, k):
    freq = Counter(words)
    return sorted(freq, key=lambda w: (-freq[w], w))[:k]

print(top_k_sorted(['i','love','leetcode','i','love','coding'], 2))
# ['i', 'love']` },
                { label:"🟢 Minimum window substring (Counter)", tip:"Counter subtraction simplifies the 'all chars covered' check.",
                    code:`from collections import Counter

def min_window(s, t):
    need    = Counter(t)
    missing = len(t)
    best    = ""
    left    = 0

    for right, c in enumerate(s):
        if need[c] > 0:
            missing -= 1  # one more required char satisfied
        need[c] -= 1

        if missing == 0:  # valid window found
            # Shrink from left while still valid
            while need[s[left]] < 0:
                need[s[left]] += 1
                left += 1
            window = s[left:right+1]
            if not best or len(window) < len(best):
                best = window
            # Break window to look for shorter one
            need[s[left]] += 1
            missing += 1
            left += 1
    return best

print(min_window("ADOBECODEBANC", "ABC"))  # "BANC"` },
            ],
        },
        {
            title:"Counter for sequence analysis",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 DNA sequence analysis", tip:"Counter on a string counts each character — perfect for nucleotide frequencies.",
                    code:`from collections import Counter

def analyze_dna(sequence):
    seq = sequence.upper()
    counts = Counter(seq)
    total = len(seq)

    print(f"Length: {total}")
    for base in 'ACGT':
        pct = counts[base] / total * 100
        print(f"  {base}: {counts[base]} ({pct:.1f}%)")
    print(f"GC content: {(counts['G']+counts['C'])/total*100:.1f}%")

analyze_dna("ATCGATCGATCGAAAA")` },
                { label:"🟠 File word count (like wc -w)", tip:"Counter on split text — efficient one-liner word count.",
                    code:`from collections import Counter
from pathlib import Path

def word_stats(text):
    words = text.lower().split()
    freq  = Counter(words)

    print(f"Total words: {len(words)}")
    print(f"Unique words: {len(freq)}")
    print(f"Top 5: {freq.most_common(5)}")

    # Words appearing only once (hapax legomena)
    unique_words = [w for w, c in freq.items() if c == 1]
    print(f"Appear once: {len(unique_words)}")

analyze_text = "to be or not to be that is the question"
word_stats(analyze_text)` },
                { label:"🟢 Intersection of two arrays (multiset)", tip:"Counter intersection keeps minimum counts — handles duplicates correctly.",
                    code:`from collections import Counter

def intersect_with_duplicates(nums1, nums2):
    """Return intersection counting multiplicity."""
    c1, c2 = Counter(nums1), Counter(nums2)

    # & takes the minimum count for each shared element
    common = c1 & c2

    # Expand back to list
    return list(common.elements())

print(intersect_with_duplicates([1,2,2,1], [2,2]))
# [2, 2]  — correctly counts multiplicity!

print(intersect_with_duplicates([4,9,5], [9,4,9,8,4]))
# [4, 9]` },
            ],
        },
    ],
};

// ── collections.OrderedDict ───────────────────────────────────
const ORDERED_DICT_DATA = {
    icon:"📒", color:"#a78bfa",
    tagline:"dict that remembers insertion order + supports move_to_end()",
    badge:"collections.OrderedDict",
    whenToUse:"LRU Cache; order-sensitive equality; reordering entries without rebuilding dict",
    declare:"from collections import OrderedDict\nod = OrderedDict()",
    complexity:{ get:"O(1)", set:"O(1)", move_to_end:"O(1)", popitem:"O(1)" },
    note:"Regular dict preserves order since Python 3.7. OrderedDict adds move_to_end() and popitem(last=)",
    methods:[
        { name:"move_to_end(key,last)", cat:"Modify",  desc:"Moves key to end (last=True) or front (last=False) — O(1). Unique to OrderedDict!", code:"od.move_to_end('a')          # → tail\nod.move_to_end('a', last=False) # → head" },
        { name:"popitem(last=True)",    cat:"Delete",  desc:"Removes and returns (key,value) — last=True pops LIFO, False pops FIFO", code:"od.popitem()          # pops last (LIFO)\nod.popitem(last=False)  # pops first (FIFO)" },
        { name:"od == od2",             cat:"Info",    desc:"OrderedDict equality IS order-sensitive (unlike regular dict)",          code:"OrderedDict([('a',1),('b',2)]) == OrderedDict([('b',2),('a',1)])\n# False — order matters!" },
        { name:"d[key] = val",          cat:"Insert",  desc:"Same as dict — preserves insertion order",                               code:"od['one'] = 1\nod['two'] = 2" },
        { name:"d.get(k, default)",     cat:"Access",  desc:"Same as dict.get — safe lookup",                                        code:"od.get('one', 0)  # 1" },
        { name:"d.items()",             cat:"Iterate", desc:"Items in insertion order — same as dict in Python 3.7+",                 code:"for k, v in od.items(): print(k, v)" },
    ],
    questions:[
        {
            title:"Implement LRU Cache with OrderedDict",
            difficulty:"Hard",
            solutions:[
                { label:"🔵 OrderedDict LRU Cache", tip:"move_to_end on every access. Oldest = front. Evict with popitem(last=False).",
                    code:`from collections import OrderedDict

class LRUCache:
    def __init__(self, capacity):
        self.cap   = capacity
        self.cache = OrderedDict()

    def get(self, key):
        if key not in self.cache:
            return -1
        # Mark as recently used — move to tail
        self.cache.move_to_end(key)
        return self.cache[key]

    def put(self, key, value):
        if key in self.cache:
            self.cache.move_to_end(key)  # already exists → refresh
        self.cache[key] = value
        if len(self.cache) > self.cap:
            # Evict oldest (front)
            self.cache.popitem(last=False)

lru = LRUCache(3)
lru.put(1,'A'); lru.put(2,'B'); lru.put(3,'C')
lru.get(1)   # access 1 → moves to tail
lru.put(4,'D')   # evicts 2 (least recent)
print(list(lru.cache.keys()))  # [3, 1, 4]` },
                { label:"🟠 Order-sensitive dict comparison", tip:"OrderedDict equality checks order; regular dict does not.",
                    code:`from collections import OrderedDict

# Regular dict: order-insensitive equality
d1 = {'a':1, 'b':2}
d2 = {'b':2, 'a':1}
print(d1 == d2)  # True — dict ignores order

# OrderedDict: order-sensitive equality
od1 = OrderedDict([('a',1),('b',2)])
od2 = OrderedDict([('b',2),('a',1)])
print(od1 == od2)  # False — order matters!

# Use when you need to verify exact sequence of config keys` },
                { label:"🟢 Reorder dict entries without rebuild", tip:"move_to_end is O(1) — avoids creating a whole new dict.",
                    code:`from collections import OrderedDict

menu = OrderedDict([
    ('starter', 8),
    ('main',   22),
    ('dessert', 9),
    ('drink',   5),
])

# Promote 'drink' to first position
menu.move_to_end('drink', last=False)
print(list(menu.keys()))  # ['drink','starter','main','dessert']

# Demote 'starter' to last
menu.move_to_end('starter')
print(list(menu.keys()))  # ['drink','main','dessert','starter']` },
            ],
        },
        {
            title:"FIFO cache and queue operations",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 FIFO cache with OrderedDict", tip:"Insert newest at end; evict oldest with popitem(last=False).",
                    code:`from collections import OrderedDict

class FIFOCache:
    def __init__(self, capacity):
        self.cap   = capacity
        self.cache = OrderedDict()

    def get(self, key):
        return self.cache.get(key, -1)
        # NOTE: No move_to_end — FIFO keeps original order

    def put(self, key, value):
        if key in self.cache:
            del self.cache[key]   # remove then re-add at tail
        self.cache[key] = value
        if len(self.cache) > self.cap:
            self.cache.popitem(last=False)  # evict OLDEST` },
                { label:"🟠 MRU Cache (most recently used eviction)", tip:"Opposite of LRU — evict MOST recent. Use popitem(last=True).",
                    code:`from collections import OrderedDict

class MRUCache:
    def __init__(self, capacity):
        self.cap = capacity
        self.cache = OrderedDict()

    def get(self, key):
        if key not in self.cache: return -1
        self.cache.move_to_end(key)  # track recency
        return self.cache[key]

    def put(self, key, value):
        if key in self.cache:
            self.cache.move_to_end(key)
        self.cache[key] = value
        if len(self.cache) > self.cap:
            self.cache.popitem(last=True)  # evict MOST recent` },
                { label:"🟢 Maintain insertion + access history", tip:"Track both when items were inserted and last accessed.",
                    code:`from collections import OrderedDict
from datetime import datetime

class AuditDict(OrderedDict):
    def __setitem__(self, key, value):
        super().__setitem__(key, {
            'value': value,
            'inserted': datetime.now().isoformat(),
            'accessed': 0
        })

    def get_value(self, key):
        if key not in self: return None
        self[key]['accessed'] += 1
        self.move_to_end(key)   # track recency
        return self[key]['value']

d = AuditDict()
d['user'] = 'Alice'
print(d.get_value('user'))  # 'Alice'
print(d['user']['accessed'])  # 1` },
            ],
        },
        {
            title:"OrderedDict for config and registry patterns",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 Plugin registry (ordered execution)", tip:"Ordered registry guarantees plugins run in registration order.",
                    code:`from collections import OrderedDict

class PluginRegistry:
    def __init__(self):
        self._plugins = OrderedDict()

    def register(self, name, plugin_fn):
        self._plugins[name] = plugin_fn
        return self  # allow chaining

    def run_all(self, data):
        for name, fn in self._plugins.items():
            print(f"Running {name}...")
            data = fn(data)
        return data

registry = PluginRegistry()
registry.register('strip',   str.strip)
registry.register('lower',   str.lower)
registry.register('replace', lambda s: s.replace(' ','-'))

result = registry.run_all("  Hello World  ")
print(result)  # 'hello-world'` },
                { label:"🟠 Last N entries log buffer", tip:"popitem(last=False) removes oldest — implements sliding window log.",
                    code:`from collections import OrderedDict

class LogBuffer:
    def __init__(self, max_entries=100):
        self.log = OrderedDict()
        self.max = max_entries
        self._seq = 0

    def add(self, message):
        self._seq += 1
        self.log[self._seq] = message
        if len(self.log) > self.max:
            self.log.popitem(last=False)  # drop oldest

    def recent(self, n=10):
        items = list(self.log.items())
        return items[-n:]

buf = LogBuffer(max_entries=3)
for msg in ['login','query','update','logout','ping']:
    buf.add(msg)
print([v for _, v in buf.recent()])  # ['update','logout','ping']` },
                { label:"🟢 Merge ordered dicts (later wins)", tip:"Merge multiple configs where later dicts override earlier ones.",
                    code:`from collections import OrderedDict

def merge_configs(*configs):
    """Merge ordered configs — later values override earlier."""
    merged = OrderedDict()
    for cfg in configs:
        merged.update(cfg)  # later keys overwrite
    return merged

defaults = OrderedDict([('timeout',30),('retries',3),('debug',False)])
production= OrderedDict([('timeout',60),('host','prod.example.com')])
override  = OrderedDict([('debug',True)])

final = merge_configs(defaults, production, override)
print(dict(final))
# {'timeout':60,'retries':3,'debug':True,'host':'prod.example.com'}` },
            ],
        },
    ],
};
// ── Stack (custom class) ──────────────────────────────────────
const STACK_DATA = {
    icon:"📚", color:"#fb923c",
    tagline:"LIFO — Last In First Out. push/pop from the same end",
    badge:"Stack (custom class wrapping list)",
    whenToUse:"Undo/redo; expression parsing; DFS; call stack simulation; backtracking",
    declare:"stack = Stack()  # or just use a list with append/pop",
    complexity:{ push:"O(1)", pop:"O(1)", peek:"O(1)", search:"O(n)" },
    note:"Python lists are already excellent stacks via append/pop(). Custom class adds clarity.",
    methods:[
        { name:"push(x)",    cat:"Insert",  desc:"Adds x to TOP of stack — O(1)",                            code:"stack.push(10)\nstack.push(20)\n# top → [10, 20]" },
        { name:"pop()",      cat:"Delete",  desc:"Removes and returns TOP element — raises IndexError if empty",code:"stack.pop()  # returns 20" },
        { name:"peek()",     cat:"Access",  desc:"Returns TOP element WITHOUT removing it",                   code:"stack.peek()  # returns 10 (still in stack)" },
        { name:"is_empty()", cat:"Info",    desc:"Returns True when stack has no elements",                   code:"stack.is_empty()  # False" },
        { name:"size()",     cat:"Info",    desc:"Returns number of elements",                                code:"stack.size()  # 1" },
        { name:"clear()",    cat:"Delete",  desc:"Removes all elements",                                      code:"stack.clear()\nstack.is_empty()  # True" },
        { name:"__repr__",   cat:"Info",    desc:"String representation showing top → bottom",                code:"print(stack)  # Stack([bottom,...,top])" },
    ],
    questions:[
        {
            title:"Stack implementation + valid parentheses",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 Custom Stack class", tip:"Wrap a list — push/pop give O(1) operations at the end.",
                    code:`class Stack:
    def __init__(self):
        self._items = []   # private — list is the underlying store

    def push(self, x):   self._items.append(x)
    def pop(self):
        if self.is_empty():
            raise IndexError("pop from empty stack")
        return self._items.pop()
    def peek(self):      return self._items[-1] if self._items else None
    def is_empty(self):  return len(self._items) == 0
    def size(self):      return len(self._items)
    def clear(self):     self._items = []
    def __repr__(self):  return f"Stack({self._items})"

# ── Classic: Valid Parentheses ───────────────────────────
def is_valid(s):
    stack = Stack()
    pairs = {')':'(', ']':'[', '}':'{'}
    for c in s:
        if c in '([{':
            stack.push(c)
        elif stack.is_empty() or stack.pop() != pairs[c]:
            return False
    return stack.is_empty()

print(is_valid('({[]})'))  # True
print(is_valid('({[)]}'))  # False` },
                { label:"🟠 Plain list as stack", tip:"Lists work perfectly as stacks — no class needed for simple cases.",
                    code:`# Python lists ARE stacks — append = push, pop() = pop
stack = []
stack.append(10)   # push
stack.append(20)
stack.append(30)

print(stack[-1])   # peek: 30
print(stack.pop()) # pop:  30
print(stack)       # [10, 20]

# Valid parens with plain list:
def is_valid(s):
    stack = []
    pairs = {')':'(', ']':'[', '}':'{'}
    for c in s:
        if c in '([{': stack.append(c)
        elif not stack or stack.pop() != pairs[c]: return False
    return not stack

print(is_valid('()[]{}'))  # True` },
                { label:"🟢 Evaluate postfix expression", tip:"Operands pushed; operators pop two and push result. O(n).",
                    code:`def eval_postfix(expression):
    stack = []
    ops = {
        '+': lambda a,b: a+b,
        '-': lambda a,b: a-b,
        '*': lambda a,b: a*b,
        '/': lambda a,b: int(a/b),
    }
    for token in expression.split():
        if token in ops:
            b, a = stack.pop(), stack.pop()  # b is top
            stack.append(ops[token](a, b))
        else:
            stack.append(int(token))
    return stack[0]

# "3 4 + 2 *" = (3+4)*2 = 14
print(eval_postfix('3 4 + 2 *'))   # 14
print(eval_postfix('5 1 2 + 4 * + 3 -'))  # 14` },
            ],
        },
        {
            title:"Undo/redo + Min Stack",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 Two-stack undo/redo", tip:"Undo stack holds history; redo holds undone states. New action clears redo.",
                    code:`class TextEditor:
    def __init__(self):
        self._text = ''
        self._undo_stack = []
        self._redo_stack = []

    def type(self, s):
        self._undo_stack.append(self._text)
        self._redo_stack.clear()   # new action clears redo
        self._text += s

    def undo(self):
        if not self._undo_stack: return 'Nothing to undo'
        self._redo_stack.append(self._text)
        self._text = self._undo_stack.pop()
        return self._text

    def redo(self):
        if not self._redo_stack: return 'Nothing to redo'
        self._undo_stack.append(self._text)
        self._text = self._redo_stack.pop()
        return self._text

    def get_text(self): return self._text

e = TextEditor()
e.type('Hello'); e.type(' World')
print(e.get_text())  # 'Hello World'
print(e.undo())      # 'Hello'
print(e.redo())      # 'Hello World'` },
                { label:"🟠 Min Stack — O(1) getMin", tip:"Parallel min stack tracks minimum at each level. Never scan to find min.",
                    code:`class MinStack:
    def __init__(self):
        self._stack = []
        self._min   = []  # parallel — tracks minimum at each level

    def push(self, val):
        self._stack.append(val)
        # New min = smaller of val or current min
        current_min = self._min[-1] if self._min else float('inf')
        self._min.append(min(val, current_min))

    def pop(self):
        self._min.pop()    # keep parallel stacks in sync
        return self._stack.pop()

    def get_min(self): return self._min[-1]  # always O(1)!

ms = MinStack()
for v in [5, 3, 7, 2, 8]:
    ms.push(v)
print(ms.get_min())  # 2
ms.pop()
print(ms.get_min())  # 3  (2 is gone)` },
                { label:"🟢 Decode string with stack", tip:"Push {count, string} objects. On ']' pop and repeat current.",
                    code:`def decode_string(s):
    stack = []
    current = ''
    num = 0

    for c in s:
        if c.isdigit():
            num = num * 10 + int(c)
        elif c == '[':
            stack.append((num, current))  # save state
            current, num = '', 0
        elif c == ']':
            count, prev = stack.pop()
            current = prev + current * count
        else:
            current += c
    return current

print(decode_string('3[a]2[bc]'))   # 'aaabcbc'
print(decode_string('2[ab3[c]]'))  # 'abcccabccc'` },
            ],
        },
        {
            title:"DFS with explicit stack + daily temperatures",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 Iterative DFS with stack", tip:"Explicit stack replaces call stack — no recursion limit issues.",
                    code:`def dfs_iterative(graph, start):
    stack   = [start]
    visited = set()
    result  = []

    while stack:
        node = stack.pop()   # LIFO = DFS behaviour
        if node in visited: continue
        visited.add(node)
        result.append(node)
        # Push neighbours in reverse so leftmost is processed first
        for neighbour in reversed(graph.get(node, [])):
            if neighbour not in visited:
                stack.append(neighbour)
    return result

graph = {'A':['B','C'],'B':['D'],'C':['D'],'D':[]}
print(dfs_iterative(graph,'A'))  # ['A','B','D','C']` },
                { label:"🟠 Daily temperatures (monotonic stack)", tip:"Decreasing monotonic stack: pop when a hotter day is found. O(n).",
                    code:`def daily_temperatures(temps):
    result = [0] * len(temps)
    stack  = []  # stores indices of days not yet resolved

    for i, temp in enumerate(temps):
        # Pop all days that are colder than today
        while stack and temps[stack[-1]] < temp:
            j = stack.pop()
            result[j] = i - j   # days waited
        stack.append(i)

    return result

print(daily_temperatures([73,74,75,71,69,72,76,73]))
# [1, 1, 4, 2, 1, 1, 0, 0]` },
                { label:"🟢 Largest rectangle in histogram", tip:"Stack of indices — pop when shorter bar found; calculate max area.",
                    code:`def largest_rectangle(heights):
    heights = heights + [0]   # sentinel 0 flushes remaining stack
    stack = []
    max_area = 0

    for i, h in enumerate(heights):
        while stack and heights[stack[-1]] > h:
            height = heights[stack.pop()]
            width  = i if not stack else i - stack[-1] - 1
            max_area = max(max_area, height * width)
        stack.append(i)

    return max_area

print(largest_rectangle([2,1,5,6,2,3]))  # 10
print(largest_rectangle([2,4]))          # 4` },
            ],
        },
    ],
};

// ── Queue (custom class) ──────────────────────────────────────
const QUEUE_DATA = {
    icon:"🎡", color:"#f472b6",
    tagline:"FIFO — First In First Out. enqueue at back, dequeue from front",
    badge:"Queue (custom class wrapping deque)",
    whenToUse:"BFS; task scheduling; rate limiting; event processing; producer-consumer pattern",
    declare:"queue = Queue()  # wraps collections.deque for O(1) dequeue",
    complexity:{ enqueue:"O(1)", dequeue:"O(1)", peek:"O(1)", search:"O(n)" },
    note:"Never use list for a queue — list.pop(0) is O(n). Always use deque or queue.Queue.",
    methods:[
        { name:"enqueue(x)",   cat:"Insert",  desc:"Adds x to the BACK of the queue — O(1)",                    code:"q.enqueue('Alice')\nq.enqueue('Bob')\n# front: Alice, back: Bob" },
        { name:"dequeue()",    cat:"Delete",  desc:"Removes and returns FRONT element — O(1) via deque",         code:"q.dequeue()  # 'Alice'" },
        { name:"peek()",       cat:"Access",  desc:"Returns FRONT element WITHOUT removing — O(1)",              code:"q.peek()  # 'Bob'" },
        { name:"is_empty()",   cat:"Info",    desc:"True when queue has no elements",                            code:"q.is_empty()  # False" },
        { name:"size()",       cat:"Info",    desc:"Number of elements in queue",                                code:"q.size()  # 1" },
        { name:"clear()",      cat:"Delete",  desc:"Removes all elements",                                       code:"q.clear()" },
        { name:"__repr__",     cat:"Info",    desc:"String representation: front → back",                        code:"print(q)  # Queue([front,...,back])" },
    ],
    questions:[
        {
            title:"Queue implementation (two ways)",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 Custom Queue wrapping deque", tip:"Deque gives O(1) enqueue and dequeue — the correct implementation.",
                    code:`from collections import deque

class Queue:
    def __init__(self):
        self._items = deque()   # deque gives O(1) popleft!

    def enqueue(self, x):  self._items.append(x)       # add to back
    def dequeue(self):
        if self.is_empty():
            raise IndexError("dequeue from empty queue")
        return self._items.popleft()  # remove from front O(1)
    def peek(self):       return self._items[0] if self._items else None
    def is_empty(self):   return len(self._items) == 0
    def size(self):       return len(self._items)
    def clear(self):      self._items.clear()
    def __repr__(self):   return f"Queue({list(self._items)})"

q = Queue()
q.enqueue('Alice')
q.enqueue('Bob')
q.enqueue('Carol')
print(q.dequeue())  # 'Alice' — FIFO
print(q.peek())     # 'Bob'
print(q.size())     # 2` },
                { label:"🟠 Queue from two stacks", tip:"Classic interview question: use two lists as stacks to simulate a queue.",
                    code:`class QueueFromStacks:
    def __init__(self):
        self._s1 = []  # incoming — enqueue here
        self._s2 = []  # outgoing — dequeue from here

    def enqueue(self, x):
        self._s1.append(x)  # always push to s1

    def dequeue(self):
        if not self._s2:
            # Transfer all from s1 to s2 (reverses order → FIFO)
            while self._s1:
                self._s2.append(self._s1.pop())
        if not self._s2:
            raise IndexError("dequeue from empty queue")
        return self._s2.pop()

    def peek(self):
        if not self._s2:
            while self._s1: self._s2.append(self._s1.pop())
        return self._s2[-1] if self._s2 else None

q = QueueFromStacks()
q.enqueue(1); q.enqueue(2); q.enqueue(3)
print(q.dequeue())  # 1 — FIFO preserved!
print(q.dequeue())  # 2` },
                { label:"🟢 Thread-safe Queue (queue.Queue)", tip:"Use queue.Queue for multi-threaded programs — it handles locking.",
                    code:`import queue

# Thread-safe queue from standard library
q = queue.Queue(maxsize=10)

q.put('task_1')    # blocks if full (with maxsize)
q.put('task_2')

print(q.get())     # 'task_1' — FIFO, blocks if empty
print(q.qsize())   # 1

# Non-blocking:
try:
    item = q.get_nowait()
except queue.Empty:
    print("Queue is empty")

# queue.LifoQueue for LIFO (stack)
# queue.PriorityQueue for priority-based` },
            ],
        },
        {
            title:"BFS and shortest path",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 BFS level-by-level", tip:"Snapshot queue length per level — processes exactly one level each iteration.",
                    code:`from collections import deque

def bfs_levels(graph, start):
    queue   = deque([start])
    visited = {start}
    levels  = []

    while queue:
        level_size = len(queue)  # nodes on this level
        level = []

        for _ in range(level_size):
            node = queue.popleft()
            level.append(node)
            for nb in graph.get(node, []):
                if nb not in visited:
                    visited.add(nb)
                    queue.append(nb)
        levels.append(level)

    return levels

g = {'A':['B','C'],'B':['D','E'],'C':['F'],'D':[],'E':[],'F':[]}
print(bfs_levels(g,'A'))
# [['A'], ['B','C'], ['D','E','F']]` },
                { label:"🟠 0-1 BFS with deque", tip:"0-1 BFS: weight-0 edges go to front (appendleft), weight-1 to back (append).",
                    code:`from collections import deque

def bfs_01(grid, start, end):
    """Shortest path in grid with 0-cost and 1-cost moves."""
    rows, cols = len(grid), len(grid[0])
    dist = [[float('inf')] * cols for _ in range(rows)]
    dist[start[0]][start[1]] = 0
    dq = deque([start])

    while dq:
        r, c = dq.popleft()
        for dr, dc in [(-1,0),(1,0),(0,-1),(0,1)]:
            nr, nc = r+dr, c+dc
            if 0<=nr<rows and 0<=nc<cols:
                cost = grid[nr][nc]
                if dist[r][c]+cost < dist[nr][nc]:
                    dist[nr][nc] = dist[r][c]+cost
                    if cost==0: dq.appendleft((nr,nc))
                    else:       dq.append((nr,nc))

    return dist[end[0]][end[1]]` },
                { label:"🟢 Producer-consumer with Queue", tip:"queue.Queue is thread-safe — ideal for producer-consumer patterns.",
                    code:`import queue
import threading
import time

def producer(q, items):
    for item in items:
        q.put(item)
        print(f"Produced: {item}")
        time.sleep(0.1)
    q.put(None)  # sentinel to signal done

def consumer(q):
    while True:
        item = q.get()
        if item is None: break
        print(f"Consumed: {item}")
        q.task_done()

q = queue.Queue(maxsize=5)
t1 = threading.Thread(target=producer, args=(q, [1,2,3,4,5]))
t2 = threading.Thread(target=consumer, args=(q,))
t1.start(); t2.start()
t1.join(); t2.join()` },
            ],
        },
        {
            title:"Sliding window max + task scheduler",
            difficulty:"Hard",
            solutions:[
                { label:"🔵 Task scheduler (queue simulation)", tip:"Use Counter for frequency, then simulate round-robin with cooldown.",
                    code:`from collections import Counter, deque

def least_interval(tasks, n):
    freq   = Counter(tasks)
    counts = sorted(freq.values(), reverse=True)
    max_f  = counts[0]
    # Slots needed = (max_freq - 1) * (n+1) + tasks_with_max_freq
    idle   = (max_f - 1) * (n + 1) + counts.count(max_f)
    return max(idle, len(tasks))

print(least_interval(['A','A','A','B','B','B'], 2))  # 8
print(least_interval(['A','A','A','B','B','B'], 0))  # 6` },
                { label:"🟠 Moving average (sliding window queue)", tip:"Fixed-size window: sum stays updated as elements enter and leave.",
                    code:`from collections import deque

class MovingAverage:
    def __init__(self, size):
        self.size   = size
        self.window = deque()
        self.total  = 0

    def next(self, val):
        self.window.append(val)
        self.total += val
        if len(self.window) > self.size:
            self.total -= self.window.popleft()
        return self.total / len(self.window)

ma = MovingAverage(3)
print(ma.next(1))   # 1.0
print(ma.next(10))  # 5.5
print(ma.next(3))   # 4.67
print(ma.next(5))   # 6.0  (window = [10,3,5])` },
                { label:"🟢 First non-repeating char in stream", tip:"Queue tracks candidate chars; dict tracks frequency.",
                    code:`from collections import deque, defaultdict

def first_unique(stream):
    freq   = defaultdict(int)
    queue  = deque()   # candidates in insertion order
    result = []

    for c in stream:
        freq[c] += 1
        queue.append(c)

        # Remove from front while front is not unique
        while queue and freq[queue[0]] > 1:
            queue.popleft()

        result.append(queue[0] if queue else '#')

    return result

stream = "aababc"
print(first_unique(stream))
# ['a','a','b','b','a','c']
# Wait — at each step: a, a, b, b, a, c` },
            ],
        },
    ],
};
// ── Algorithms ────────────────────────────────────────────────
const ALGORITHMS_DATA = {
    icon:"⚡", color:"#60a5fa",
    tagline:"Core algorithm patterns — master these to ace any Python interview",
    badge:"Algorithm Patterns (Python)",
    whenToUse:"Match the pattern to the problem shape — then write the code",
    declare:"# No single declaration — each pattern has its own structure",
    complexity:{ time:"varies", space:"varies", best:"O(n)", worst:"O(n²)" },
    note:"Recognising the PATTERN matters more than memorising the exact code",
    methods:[],
    questions:[
        {
            title:"Binary Search — find target in sorted list",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 Iterative (preferred)", tip:"bisect module is built-in — use it in production. Manual version for interviews.",
                    code:`import bisect

# ── Production: use bisect module ───────────────────────
nums = [2, 5, 8, 12, 16, 23, 38, 56]
idx  = bisect.bisect_left(nums, 23)
found = idx < len(nums) and nums[idx] == 23
print(found, idx)  # True 5

# ── Interview: manual iterative ─────────────────────────
def binary_search(arr, target):
    left, right = 0, len(arr) - 1
    while left <= right:
        mid = (left + right) // 2   # no overflow in Python
        if   arr[mid] == target: return mid
        elif arr[mid] <  target: left  = mid + 1
        else:                    right = mid - 1
    return -1

print(binary_search([2,5,8,12,16,23,38,56], 23))  # 5
print(binary_search([2,5,8,12,16,23,38,56], 10))  # -1` },
                { label:"🟠 Search in rotated sorted array", tip:"One half is always sorted — check which half, then decide which to search.",
                    code:`def search_rotated(nums, target):
    left, right = 0, len(nums) - 1

    while left <= right:
        mid = (left + right) // 2
        if nums[mid] == target: return mid

        # Determine which half is sorted
        if nums[left] <= nums[mid]:  # left half is sorted
            if nums[left] <= target < nums[mid]:
                right = mid - 1   # target in left half
            else:
                left = mid + 1    # target in right half
        else:                        # right half is sorted
            if nums[mid] < target <= nums[right]:
                left = mid + 1    # target in right half
            else:
                right = mid - 1   # target in left half
    return -1

print(search_rotated([4,5,6,7,0,1,2], 0))  # 4
print(search_rotated([4,5,6,7,0,1,2], 3))  # -1` },
                { label:"🟢 Binary search on answer (find minimum speed)", tip:"When search space is a range of answers, not an array — same template.",
                    code:`import math

def min_eating_speed(piles, h):
    """Minimum speed k to eat all piles in h hours."""

    def can_finish(k):
        return sum(math.ceil(p / k) for p in piles) <= h

    left, right = 1, max(piles)
    while left < right:
        mid = (left + right) // 2
        if can_finish(mid):
            right = mid   # try smaller speed
        else:
            left = mid + 1  # need faster speed
    return left

print(min_eating_speed([3,6,7,11], 8))   # 4
print(min_eating_speed([30,11,23,4,20],5))  # 30` },
            ],
        },
        {
            title:"Sorting — built-in and custom key functions",
            difficulty:"Easy",
            solutions:[
                { label:"🔵 sorted() and list.sort() with key=", tip:"key= takes a function returning the sort value. Avoids defining full comparator.",
                    code:`# ── sorted() returns a NEW list ──────────────────────────
nums = [3, 1, 4, 1, 5, 9, 2, 6]
print(sorted(nums))              # ascending: [1,1,2,3,4,5,6,9]
print(sorted(nums, reverse=True)) # descending

# ── list.sort() mutates IN PLACE ─────────────────────────
nums.sort()     # mutates nums — returns None!

# ── Custom key= function ─────────────────────────────────
people = [('Alice',30), ('Bob',25), ('Carol',35)]
people.sort(key=lambda p: p[1])        # sort by age
people.sort(key=lambda p: (-p[1], p[0]))  # age desc, then name asc

# ── Sort strings case-insensitively ──────────────────────
words = ['Banana','apple','Cherry']
print(sorted(words, key=str.lower))    # ['apple','Banana','Cherry']

# ── Sort dict by value ────────────────────────────────────
d = {'b':3,'a':1,'c':2}
print(sorted(d, key=d.get))           # ['a','c','b']
print(sorted(d.items(), key=lambda x: x[1]))  # [('a',1),('c',2),('b',3)]` },
                { label:"🟠 Quicksort implementation", tip:"Average O(n log n). Use middle element as pivot to avoid worst-case on sorted input.",
                    code:`def quicksort(arr):
    if len(arr) <= 1:
        return arr

    pivot = arr[len(arr) // 2]  # middle pivot
    left  = [x for x in arr if x < pivot]
    mid   = [x for x in arr if x == pivot]
    right = [x for x in arr if x > pivot]

    return quicksort(left) + mid + quicksort(right)

print(quicksort([3,6,8,10,1,2,1]))
# [1, 1, 2, 3, 6, 8, 10]` },
                { label:"🟢 Merge sort implementation", tip:"Always O(n log n), stable. Best when stability (relative order of equals) matters.",
                    code:`def merge_sort(arr):
    if len(arr) <= 1:
        return arr

    mid   = len(arr) // 2
    left  = merge_sort(arr[:mid])
    right = merge_sort(arr[mid:])
    return merge(left, right)

def merge(left, right):
    result = []
    l = r = 0
    while l < len(left) and r < len(right):
        if left[l] <= right[r]:
            result.append(left[l]); l += 1
        else:
            result.append(right[r]); r += 1
    return result + left[l:] + right[r:]

print(merge_sort([38,27,43,3,9,82,10]))
# [3, 9, 10, 27, 38, 43, 82]` },
            ],
        },
        {
            title:"Two Pointers — container with most water",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 Two pointers converge", tip:"Move shorter pointer inward — only way to potentially find larger area. O(n).",
                    code:`def max_water(height):
    left, right   = 0, len(height) - 1
    max_area      = 0

    while left < right:
        width    = right - left
        area     = width * min(height[left], height[right])
        max_area = max(max_area, area)

        # Move the SHORTER pointer — it's the limiting factor
        if height[left] < height[right]:
            left  += 1
        else:
            right -= 1

    return max_area

print(max_water([1,8,6,2,5,4,8,3,7]))  # 49` },
                { label:"🟠 Three sum", tip:"Sort first, fix one element, use two pointers on remainder. Skip duplicates carefully.",
                    code:`def three_sum(nums):
    nums.sort()
    result = []

    for i in range(len(nums) - 2):
        if i > 0 and nums[i] == nums[i-1]:
            continue  # skip duplicate fixed element

        left, right = i+1, len(nums)-1
        while left < right:
            total = nums[i] + nums[left] + nums[right]
            if total == 0:
                result.append([nums[i], nums[left], nums[right]])
                while left < right and nums[left]  == nums[left+1]:  left  += 1
                while left < right and nums[right] == nums[right-1]: right -= 1
                left += 1; right -= 1
            elif total < 0: left  += 1
            else:           right -= 1
    return result

print(three_sum([-1,0,1,2,-1,-4]))
# [[-1,-1,2],[-1,0,1]]` },
                { label:"🟢 Remove duplicates in-place", tip:"Slow pointer marks write position; fast pointer scans for unique values.",
                    code:`def remove_duplicates(nums):
    if not nums: return 0
    slow = 0  # write pointer

    for fast in range(1, len(nums)):
        if nums[fast] != nums[slow]:
            slow += 1
            nums[slow] = nums[fast]

    return slow + 1  # length of unique portion

nums = [1,1,2,3,3,4,5,5]
k = remove_duplicates(nums)
print(nums[:k])  # [1, 2, 3, 4, 5]` },
            ],
        },
        {
            title:"Sliding Window — longest substring without repeat",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 Dict + variable window", tip:"Dict stores char → latest index. Jump left pointer directly past duplicate.",
                    code:`def length_of_longest_substring(s):
    last_seen = {}   # char → most recent index
    max_len   = 0
    left      = 0

    for right, char in enumerate(s):
        # If char is in window, jump left past its previous position
        if char in last_seen and last_seen[char] >= left:
            left = last_seen[char] + 1

        last_seen[char] = right
        max_len = max(max_len, right - left + 1)

    return max_len

print(length_of_longest_substring('abcabcbb'))  # 3 ("abc")
print(length_of_longest_substring('pwwkew'))    # 3 ("wke")` },
                { label:"🟠 Set + shrink window", tip:"Set tracks current window. Remove from left until no duplicate. O(n).",
                    code:`def length_of_longest_substring(s):
    window  = set()
    max_len = 0
    left    = 0

    for right in range(len(s)):
        while s[right] in window:   # shrink until unique
            window.discard(s[left])
            left += 1
        window.add(s[right])
        max_len = max(max_len, right - left + 1)

    return max_len` },
                { label:"🟢 Max sum subarray of fixed size k", tip:"Add right element, subtract left element. O(1) window update.",
                    code:`def max_sum_subarray(arr, k):
    if len(arr) < k: return None

    # Build first window
    window_sum = sum(arr[:k])
    max_sum    = window_sum

    for i in range(k, len(arr)):
        window_sum += arr[i] - arr[i-k]   # slide: add new, drop old
        max_sum = max(max_sum, window_sum)

    return max_sum

print(max_sum_subarray([2,1,5,1,3,2], 3))  # 9  (5+1+3)
print(max_sum_subarray([2,3,4,1,5],   2))  # 7  (3+4)` },
            ],
        },
        {
            title:"Dict/Counter Pattern — subarray sum equals k",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 Prefix sum + dict", tip:"If (prefix_sum - k) seen before → subarray ending here sums to k. O(n).",
                    code:`def subarray_sum(nums, k):
    # prefix_count: how many times each prefix sum has been seen
    prefix_count = {0: 1}  # empty prefix has sum 0
    total  = 0
    count  = 0

    for num in nums:
        total += num
        # Times we can end a valid subarray HERE
        count += prefix_count.get(total - k, 0)
        prefix_count[total] = prefix_count.get(total, 0) + 1

    return count

print(subarray_sum([1,1,1], 2))         # 2
print(subarray_sum([1,2,3], 3))         # 2  ([1,2] and [3])
print(subarray_sum([3,4,7,2,-3,1,4,2], 7))  # 4` },
                { label:"🟠 Longest consecutive sequence", tip:"Put all nums in a set. For each sequence start (n-1 not in set), count length.",
                    code:`def longest_consecutive(nums):
    num_set = set(nums)   # O(1) lookups
    longest = 0

    for num in num_set:
        # Only start counting at SEQUENCE BEGINNINGS
        if num - 1 not in num_set:
            length  = 1
            current = num
            while current + 1 in num_set:
                current += 1
                length  += 1
            longest = max(longest, length)

    return longest

print(longest_consecutive([100,4,200,1,3,2]))          # 4 (1,2,3,4)
print(longest_consecutive([0,3,7,2,5,8,4,6,0,1]))     # 9` },
                { label:"🟢 Four sum count (4 arrays)", tip:"Split into two pairs — count A+B sums in dict, then look up -(C+D).",
                    code:`from collections import defaultdict

def four_sum_count(a, b, c, d):
    ab_sums = defaultdict(int)
    for x in a:
        for y in b:
            ab_sums[x+y] += 1

    count = 0
    for x in c:
        for y in d:
            count += ab_sums[-(x+y)]   # need -(c+d) in ab_sums
    return count

print(four_sum_count([1,2],[-2,-1],[-1,2],[0,2]))  # 2` },
            ],
        },
        {
            title:"Recursion & Backtracking — generate permutations",
            difficulty:"Medium",
            solutions:[
                { label:"🔵 Backtracking template", tip:"CHOOSE → EXPLORE → UN-CHOOSE. This exact pattern solves most backtracking problems.",
                    code:`def permutations(nums):
    result  = []
    current = []
    used    = [False] * len(nums)

    def backtrack():
        if len(current) == len(nums):
            result.append(current[:])  # snapshot — not reference!
            return

        for i in range(len(nums)):
            if used[i]: continue

            # ── CHOOSE ──────────────────────────────
            current.append(nums[i])
            used[i] = True

            # ── EXPLORE ─────────────────────────────
            backtrack()

            # ── UN-CHOOSE (backtrack) ────────────────
            current.pop()
            used[i] = False

    backtrack()
    return result

print(permutations([1,2,3]))
# [[1,2,3],[1,3,2],[2,1,3],[2,3,1],[3,1,2],[3,2,1]]` },
                { label:"🟠 Generate all subsets (power set)", tip:"At each index: include current OR skip it. Produces 2^n subsets.",
                    code:`def subsets(nums):
    result  = []
    current = []

    def backtrack(start):
        result.append(current[:])   # every partial state is valid

        for i in range(start, len(nums)):
            current.append(nums[i])   # choose
            backtrack(i + 1)          # explore further
            current.pop()             # un-choose

    backtrack(0)
    return result

print(subsets([1,2,3]))
# [[], [1], [1,2], [1,2,3], [1,3], [2], [2,3], [3]]

# Pythonic one-liner with itertools:
from itertools import chain, combinations
def subsets_builtin(nums):
    return list(chain.from_iterable(
        combinations(nums, r) for r in range(len(nums)+1)
    ))` },
                { label:"🟢 Memoized recursion (functools.lru_cache)", tip:"@lru_cache or @cache decorator adds memoization automatically — O(n) not O(2^n).",
                    code:`from functools import lru_cache, cache

# @cache is Python 3.9+ — same as @lru_cache(maxsize=None)
@cache
def fib(n):
    if n <= 1: return n
    return fib(n-1) + fib(n-2)

print(fib(10))   # 55
print(fib(50))   # 12586269025 — instant with memoization

# Manual memo dict (for interview when decorator not allowed):
def fib_manual(n, memo={}):
    if n <= 1: return n
    if n not in memo:
        memo[n] = fib_manual(n-1, memo) + fib_manual(n-2, memo)
    return memo[n]

# Coin change (bottom-up DP for comparison):
def coin_change(coins, amount):
    dp = [float('inf')] * (amount + 1)
    dp[0] = 0
    for i in range(1, amount+1):
        for c in coins:
            if c <= i:
                dp[i] = min(dp[i], dp[i-c] + 1)
    return dp[amount] if dp[amount] != float('inf') else -1

print(coin_change([1,5,11], 15))  # 3  (5+5+5)` },
            ],
        },
    ],
};

// ── ALL_TOPICS registry ───────────────────────────────────────
// Order here = order in the sidebar.
// Adding a new topic = add ONE entry here.
const ALL_TOPICS = [
    { id:"list",         label:"list",          data:LIST_DATA,         icon:<ListAltIcon /> },
    { id:"tuple",        label:"tuple",         data:TUPLE_DATA,        icon:<TuneIcon /> },
    { id:"set",          label:"set",           data:SET_DATA,          icon:<FilterListIcon /> },
    { id:"frozenset",    label:"frozenset",      data:FROZENSET_DATA,    icon:<LockIcon /> },
    { id:"dict",         label:"dict",          data:DICT_DATA,         icon:<GridOnIcon /> },
    { id:"deque",        label:"deque",         data:DEQUE_DATA,        icon:<LinkIcon /> },
    { id:"defaultdict",  label:"defaultdict",   data:DEFAULTDICT_DATA,  icon:<RepeatIcon /> },
    { id:"counter",      label:"Counter",       data:COUNTER_DATA,      icon:<BarChartIcon /> },
    { id:"ordereddict",  label:"OrderedDict",   data:ORDERED_DICT_DATA, icon:<SortIcon /> },
    { id:"stack",        label:"Stack",         data:STACK_DATA,        icon:<LayersIcon /> },
    { id:"queue",        label:"Queue",         data:QUEUE_DATA,        icon:<ViewStreamIcon /> },
    { id:"algorithms",   label:"Algorithms",    data:ALGORITHMS_DATA,   icon:<FunctionsIcon /> },
];
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SECTION 3 — COMPONENTS
// Each component has ONE responsibility.
// They receive props and render — no cross-component state.
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/**
 * ComplexityBadge — colour-coded O(n) complexity pill.
 * Green=O(1), Amber=O(log n), Red=O(n), Grey=varies/N/A
 */
const ComplexityBadge = ({ operation, value }) => {
    const getColor = (v) => {
        if (v.includes("1)"))        return APP_COLORS.success;
        if (v.includes("log"))       return APP_COLORS.warning;
        if (v.includes("varies") || v.includes("N/A")) return APP_COLORS.textMuted;
        return APP_COLORS.danger;
    };
    return (
        <Box sx={{ textAlign:"center", border:`1px solid ${APP_COLORS.border}`, borderRadius:1.5, px:1.5, py:0.5, minWidth:64, bgcolor:"rgba(0,0,0,0.3)" }}>
            <Typography variant="caption" sx={{ color:APP_COLORS.textMuted, display:"block", fontSize:9, textTransform:"uppercase" }}>{operation}</Typography>
            <Typography variant="caption" sx={{ color:getColor(value), fontWeight:700, fontSize:11 }}>{value}</Typography>
        </Box>
    );
};

/**
 * CategoryChip — coloured label for method categories.
 */
const CategoryChip = ({ category }) => {
    const style = CATEGORY_STYLE_MAP[category] || { bg:"#1e2d3d", color:"#8b949e" };
    return (
        <Chip label={category} size="small" sx={{ bgcolor:style.bg, color:style.color, fontWeight:700, fontSize:9, height:20, "& .MuiChip-label":{ px:1 } }} />
    );
};

/**
 * CopyButton — clipboard copy with 1.5 s checkmark feedback.
 */
const CopyButton = ({ textToCopy, copyId, copiedId, onCopy }) => {
    const isCopied = copiedId === copyId;
    return (
        <Tooltip title={isCopied ? "Copied!" : "Copy code"}>
            <IconButton size="small"
                        onClick={() => { navigator.clipboard.writeText(textToCopy); onCopy(copyId); }}
                        sx={{ color:isCopied ? APP_COLORS.success : APP_COLORS.textMuted, "&:hover":{ color:APP_COLORS.title }, transition:"color 0.2s" }}>
                {isCopied ? <CheckIcon sx={{ fontSize:14 }} /> : <ContentCopyIcon sx={{ fontSize:14 }} />}
            </IconButton>
        </Tooltip>
    );
};

/**
 * CodeBlock — syntax-highlighted code panel with CopyButton.
 */
const CodeBlock = ({ code, copyId, copiedId, onCopy }) => (
    <Box sx={{ position:"relative", mt:1 }}>
        <Box sx={{ position:"absolute", top:4, right:4, zIndex:1 }}>
            <CopyButton textToCopy={code} copyId={copyId} copiedId={copiedId} onCopy={onCopy} />
        </Box>
        <Box component="pre" sx={{
            bgcolor:APP_COLORS.codeBg, border:`1px solid ${APP_COLORS.border}`, borderRadius:1.5,
            p:"12px 36px 12px 14px", fontSize:11, lineHeight:1.85, color:APP_COLORS.text,
            overflowX:"auto", m:0, whiteSpace:"pre",
            fontFamily:"'JetBrains Mono','Fira Code','Courier New',monospace",
        }}>
            {code}
        </Box>
    </Box>
);

/**
 * MethodCard — single method: name, category chip, description, code.
 */
const MethodCard = ({ method, color, cardIndex, copiedId, onCopy }) => (
    <Card sx={{ bgcolor:APP_COLORS.surface, border:`1px solid ${APP_COLORS.border}`, borderRadius:2, transition:"border-color 0.2s, transform 0.15s", "&:hover":{ borderColor:color, transform:"translateY(-1px)" } }}>
        <CardContent sx={{ p:"12px !important" }}>
            <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"center", mb:0.75 }}>
                <Typography variant="caption" sx={{ color, fontWeight:700, fontSize:12, fontFamily:"monospace" }}>{method.name}</Typography>
                <CategoryChip category={method.cat} />
            </Box>
            <Typography variant="caption" sx={{ color:APP_COLORS.textMuted, display:"block", mb:0.75, lineHeight:1.5, fontSize:10.5 }}>{method.desc}</Typography>
            <CodeBlock code={method.code} copyId={`method-${cardIndex}`} copiedId={copiedId} onCopy={onCopy} />
        </CardContent>
    </Card>
);

/**
 * SolutionPanel — one practice problem with 3 solution tabs + comparison accordion.
 */
const SolutionPanel = ({ question, color, copiedId, onCopy }) => {
    const [activeSolution, setActiveSolution] = useState(0);
    const diffColor = DIFFICULTY_COLOR_MAP[question.difficulty] || APP_COLORS.textMuted;
    const current   = question.solutions[activeSolution];
    return (
        <Card sx={{ bgcolor:APP_COLORS.surface, border:`1px solid ${color}44`, borderRadius:2, overflow:"hidden" }}>
            <CardContent sx={{ p:"0 !important" }}>
                {/* Header */}
                <Box sx={{ display:"flex", alignItems:"center", gap:1.5, p:"12px 16px", bgcolor:"rgba(0,0,0,0.3)", borderBottom:`1px solid ${APP_COLORS.border}` }}>
                    <CodeIcon sx={{ color, fontSize:18 }} />
                    <Box>
                        <Typography sx={{ fontWeight:700, fontSize:13, color:APP_COLORS.text }}>{question.title}</Typography>
                        <Typography sx={{ fontSize:10, color:diffColor }}>● {question.difficulty} · 3 solutions</Typography>
                    </Box>
                </Box>
                {/* Solution tabs */}
                <Tabs value={activeSolution} onChange={(_,v) => setActiveSolution(v)} sx={{ bgcolor:"rgba(0,0,0,0.2)", borderBottom:`1px solid ${APP_COLORS.border}`, minHeight:38, "& .MuiTab-root":{ minHeight:38, fontSize:10, fontWeight:600, color:APP_COLORS.textMuted }, "& .Mui-selected":{ color:`${color} !important` }, "& .MuiTabs-indicator":{ bgcolor:color } }}>
                    {question.solutions.map((sol,i) => <Tab key={i} label={sol.label} />)}
                </Tabs>
                {/* Strategy tip */}
                <Box sx={{ p:"10px 16px", bgcolor:`${color}0d`, borderBottom:`1px solid ${APP_COLORS.border}` }}>
                    <Typography sx={{ fontSize:11, color:APP_COLORS.text }}>
                        <span style={{ color, fontWeight:700 }}>💬 Strategy: </span>{current.tip}
                    </Typography>
                </Box>
                {/* Code */}
                <Box sx={{ p:2 }}>
                    <CodeBlock code={current.code} copyId={`sol-${question.title}-${activeSolution}`} copiedId={copiedId} onCopy={onCopy} />
                </Box>
                {/* When to use accordion */}
                <Accordion disableGutters sx={{ bgcolor:"transparent", boxShadow:"none", borderTop:`1px solid ${APP_COLORS.border}`, "&:before":{ display:"none" } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color:APP_COLORS.textMuted, fontSize:16 }} />} sx={{ px:2, py:0.5, minHeight:36 }}>
                        <Typography sx={{ fontSize:10, fontWeight:700, color:APP_COLORS.textMuted, letterSpacing:"0.06em" }}>⚖️ WHEN TO USE EACH APPROACH</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px:2, pb:2, pt:0 }}>
                        {question.solutions.map((sol,i) => (
                            <Box key={i} sx={{ display:"flex", gap:1.5, mb:i<2?1.5:0 }}>
                                <Typography sx={{ fontSize:14, minWidth:22 }}>{["🔵","🟠","🟢"][i]}</Typography>
                                <Box>
                                    <Typography sx={{ fontSize:11, fontWeight:600, color:APP_COLORS.text }}>{sol.label.replace(/^[^ ]+ /,"")}</Typography>
                                    <Typography sx={{ fontSize:10, color:APP_COLORS.textMuted, mt:0.25 }}>{sol.tip}</Typography>
                                </Box>
                            </Box>
                        ))}
                    </AccordionDetails>
                </Accordion>
            </CardContent>
        </Card>
    );
};

/**
 * InfoBar — topic header: icon, tagline, declaration, complexity badges, when-to-use.
 */
const InfoBar = ({ topicData }) => (
    <Box sx={{ mb:3 }}>
        <Box sx={{ display:"flex", alignItems:"center", gap:1, mb:0.5 }}>
            <Typography sx={{ fontSize:22 }}>{topicData.icon}</Typography>
            <Box>
                <Typography sx={{ fontSize:15, fontWeight:900, color:APP_COLORS.title }}>{topicData.badge}</Typography>
                <Typography sx={{ fontSize:11, color:APP_COLORS.textMuted }}>{topicData.tagline}</Typography>
            </Box>
        </Box>
        <Box sx={{ display:"inline-block", bgcolor:"rgba(0,0,0,0.4)", border:`1px solid ${APP_COLORS.border}`, borderRadius:1, px:1.5, py:0.5, mb:1.5 }}>
            <Typography component="code" sx={{ fontSize:11, color:topicData.color, fontFamily:"monospace", whiteSpace:"pre" }}>{topicData.declare}</Typography>
        </Box>
        <Box sx={{ display:"flex", flexWrap:"wrap", gap:1, alignItems:"flex-start" }}>
            {Object.entries(topicData.complexity).map(([op,val]) => (
                <ComplexityBadge key={op} operation={op} value={val} />
            ))}
            <Box sx={{ flex:1, minWidth:180, bgcolor:`${topicData.color}10`, border:`1px solid ${topicData.color}33`, borderRadius:1.5, px:1.5, py:0.75 }}>
                <Typography sx={{ fontSize:10, color:topicData.color, fontWeight:700 }}>💡 Use when:</Typography>
                <Typography sx={{ fontSize:10.5, color:APP_COLORS.text }}>{topicData.whenToUse}</Typography>
            </Box>
        </Box>
        {topicData.note && (
            <Typography sx={{ fontSize:10, color:APP_COLORS.textMuted, mt:0.75, fontStyle:"italic" }}>
                * {topicData.note}
            </Typography>
        )}
    </Box>
);

/**
 * MethodsTab — filterable grid of method cards.
 */
const MethodsTab = ({ topicData, copiedId, onCopy }) => {
    const [activeCategory, setActiveCategory] = useState("All");
    const categories = useMemo(() => ["All", ...new Set(topicData.methods.map(m => m.cat))], [topicData.methods]);
    const visible    = useMemo(() => activeCategory === "All" ? topicData.methods : topicData.methods.filter(m => m.cat === activeCategory), [topicData.methods, activeCategory]);

    if (topicData.methods.length === 0) {
        return (
            <Box sx={{ textAlign:"center", py:6, color:APP_COLORS.textMuted }}>
                <SchoolIcon sx={{ fontSize:40, mb:1, opacity:0.4 }} />
                <Typography sx={{ fontSize:13 }}>No individual methods — see the Practice tab for algorithm patterns.</Typography>
            </Box>
        );
    }
    return (
        <Box>
            <Box sx={{ display:"flex", flexWrap:"wrap", gap:1, mb:2.5 }}>
                {categories.map(cat => (
                    <Chip key={cat} label={cat} onClick={() => setActiveCategory(cat)} variant={activeCategory===cat?"filled":"outlined"} size="small"
                          sx={{ cursor:"pointer", fontSize:10, fontWeight:600, color:activeCategory===cat?APP_COLORS.background:APP_COLORS.textMuted, bgcolor:activeCategory===cat?topicData.color:"transparent", borderColor:activeCategory===cat?topicData.color:APP_COLORS.border, "&:hover":{ borderColor:topicData.color, color:topicData.color } }} />
                ))}
            </Box>
            <Box sx={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(260px, 1fr))", gap:1.5 }}>
                {visible.map((method,i) => (
                    <MethodCard key={method.name} method={method} color={topicData.color} cardIndex={`${topicData.badge}-${i}`} copiedId={copiedId} onCopy={onCopy} />
                ))}
            </Box>
        </Box>
    );
};

/**
 * PracticeTab — question selector pills + active solution panel.
 */
const PracticeTab = ({ topicData, copiedId, onCopy }) => {
    const [activeQuestion, setActiveQuestion] = useState(0);
    const current = topicData.questions[activeQuestion];
    return (
        <Box>
            <Box sx={{ display:"flex", flexWrap:"wrap", gap:1, mb:3 }}>
                {topicData.questions.map((q,i) => {
                    const dc = DIFFICULTY_COLOR_MAP[q.difficulty] || APP_COLORS.textMuted;
                    const isActive = activeQuestion === i;
                    return (
                        <Button key={i} onClick={() => setActiveQuestion(i)} variant={isActive?"contained":"outlined"} size="small"
                                sx={{ textTransform:"none", fontSize:11, fontWeight:700, fontFamily:"monospace", borderColor:isActive?topicData.color:APP_COLORS.border, bgcolor:isActive?`${topicData.color}22`:"transparent", color:isActive?topicData.color:APP_COLORS.textMuted, "&:hover":{ borderColor:topicData.color, bgcolor:`${topicData.color}11` }, display:"flex", flexDirection:"column", alignItems:"flex-start", px:1.5, py:0.75, lineHeight:1.4 }}>
                            <span>#{i+1} {q.title}</span>
                            <span style={{ fontSize:9, color:dc }}>● {q.difficulty}</span>
                        </Button>
                    );
                })}
            </Box>
            {current && <SolutionPanel question={current} color={topicData.color} copiedId={copiedId} onCopy={onCopy} />}
        </Box>
    );
};

/**
 * TopicContent — Methods/Practice tab switcher for one topic.
 * key={activeTopicId} on parent forces remount → resets tabs automatically.
 */
const TopicContent = ({ topicData, copiedId, onCopy }) => {
    const [activeTab, setActiveTab] = useState(topicData.methods.length === 0 ? 1 : 0);
    return (
        <Box>
            <InfoBar topicData={topicData} />
            <Tabs value={activeTab} onChange={(_,v) => setActiveTab(v)} sx={{ mb:3, borderBottom:`1px solid ${APP_COLORS.border}`, "& .MuiTab-root":{ fontSize:11, fontWeight:700, color:APP_COLORS.textMuted, textTransform:"uppercase", letterSpacing:"0.05em", minHeight:40 }, "& .Mui-selected":{ color:`${topicData.color} !important` }, "& .MuiTabs-indicator":{ bgcolor:topicData.color } }}>
                <Tab label="📋 Methods" disabled={topicData.methods.length === 0} />
                <Tab label="🧩 Practice" />
            </Tabs>
            {activeTab === 0 && <MethodsTab topicData={topicData} copiedId={copiedId} onCopy={onCopy} />}
            {activeTab === 1 && <PracticeTab topicData={topicData} copiedId={copiedId} onCopy={onCopy} />}
        </Box>
    );
};

/**
 * SidebarContent — navigation list.
 * onSelect lets the parent close the mobile drawer after navigation.
 */
const SidebarContent = ({ activeTopicId, onSelect }) => (
    <Box sx={{ display:"flex", flexDirection:"column", height:"100%" }}>
        {/* Logo */}
        <Box sx={{ p:2, pb:1.5 }}>
            <Box sx={{ display:"flex", alignItems:"center", gap:1, mb:0.25 }}>
                <Box sx={{ background:"linear-gradient(135deg,#3776ab,#ffd343)", borderRadius:0.75, px:0.75, py:0.3 }}>
                    <Typography sx={{ fontWeight:900, fontSize:11, color:"#fff", lineHeight:1 }}>Py</Typography>
                </Box>
                <Typography sx={{ fontWeight:900, fontSize:13, color:APP_COLORS.title, lineHeight:1.2 }}>Python</Typography>
            </Box>
            <Typography sx={{ fontSize:10, color:APP_COLORS.textMuted, pl:"34px" }}>Complete Algorithms</Typography>
        </Box>
        <Divider sx={{ borderColor:APP_COLORS.border }} />
        {/* Navigation items */}
        <List dense sx={{ flex:1, py:1, overflowY:"auto" }}>
            {ALL_TOPICS.map(topic => {
                const isActive = activeTopicId === topic.id;
                return (
                    <ListItem key={topic.id} disablePadding>
                        <ListItemButton onClick={() => onSelect(topic.id)} sx={{ borderRadius:1.5, mx:1, my:0.25, bgcolor:isActive?`${topic.data.color}22`:"transparent", borderLeft:isActive?`3px solid ${topic.data.color}`:"3px solid transparent", "&:hover":{ bgcolor:`${topic.data.color}11` }, transition:"all 0.18s", py:0.75 }}>
                            <ListItemIcon sx={{ minWidth:32, color:isActive?topic.data.color:APP_COLORS.textMuted }}>{topic.data.icon}</ListItemIcon>
                            <ListItemText primary={topic.label} primaryTypographyProps={{ fontSize:12, fontWeight:isActive?700:400, color:isActive?topic.data.color:APP_COLORS.text }} />
                            {isActive && <BookmarkIcon sx={{ fontSize:12, color:topic.data.color }} />}
                        </ListItemButton>
                    </ListItem>
                );
            })}
        </List>
        <Divider sx={{ borderColor:APP_COLORS.border }} />
        {/* Footer */}
        <Box sx={{ p:1.5, textAlign:"center" }}>
            <Box sx={{ display:"flex", justifyContent:"center", alignItems:"center", gap:1, mb:0.5 }}>
                <Box sx={{ background:"linear-gradient(135deg,#3776ab,#ffd343)", borderRadius:0.5, px:0.5, py:0.1 }}>
                    <Typography sx={{ fontWeight:900, fontSize:9, color:"#fff" }}>Py</Typography>
                </Box>
                <Typography sx={{ fontSize:9, color:APP_COLORS.textMuted }}>Python Algo Prep</Typography>
            </Box>
            <Typography sx={{ fontSize:9, color:APP_COLORS.textMuted }}>12 Structures · 6 Algorithm Patterns</Typography>
        </Box>
    </Box>
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SECTION 4 — ROOT APP COMPONENT
// Three pieces of global state:
//   1. activeTopicId    — which topic shows in main area
//   2. mobileDrawerOpen — sidebar visibility on mobile
//   3. copiedId         — which code block was recently copied
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/**
 * AppContent — AppBar + Drawer + main content area.
 * Separated from root so MUI theme is active when it renders.
 */
const AppContent = () => {
    const theme    = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [activeTopicId,    setActiveTopicId]    = useState("list");
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const [copiedId,         setCopiedId]         = useState(null);

    const activeTopic = ALL_TOPICS.find(t => t.id === activeTopicId);

    const handleTopicSelect = (id) => {
        setActiveTopicId(id);
        if (isMobile) setMobileDrawerOpen(false);
    };

    const handleCopy = (id) => {
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 1500);
    };

    const drawerContent = <SidebarContent activeTopicId={activeTopicId} onSelect={handleTopicSelect} />;

    return (
        <Box sx={{ display:"flex", minHeight:"100vh", bgcolor:APP_COLORS.background }}>
            {/* Mobile AppBar */}
            {isMobile && (
                <AppBar position="fixed" sx={{ bgcolor:APP_COLORS.drawerBg, borderBottom:`1px solid ${APP_COLORS.border}`, zIndex:theme.zIndex.drawer+1, boxShadow:"none" }}>
                    <Toolbar variant="dense" sx={{ gap:1, minHeight:48 }}>
                        <IconButton edge="start" size="small" onClick={() => setMobileDrawerOpen(true)} sx={{ color:APP_COLORS.title }}>
                            <MenuIcon />
                        </IconButton>
                        <Typography sx={{ fontWeight:700, fontSize:13, color:APP_COLORS.title, flex:1 }}>{activeTopic?.label ?? "Python"}</Typography>
                        <Typography sx={{ fontSize:18 }}>{activeTopic?.data.icon}</Typography>
                    </Toolbar>
                </AppBar>
            )}
            {/* Desktop permanent sidebar */}
            {!isMobile && (
                <Drawer variant="permanent" sx={{ width:DRAWER_WIDTH, flexShrink:0, "& .MuiDrawer-paper":{ width:DRAWER_WIDTH, bgcolor:APP_COLORS.drawerBg, borderRight:`1px solid ${APP_COLORS.border}`, overflowX:"hidden" } }}>
                    {drawerContent}
                </Drawer>
            )}
            {/* Mobile temporary drawer */}
            {isMobile && (
                <Drawer variant="temporary" open={mobileDrawerOpen} onClose={() => setMobileDrawerOpen(false)} ModalProps={{ keepMounted:true }}
                        sx={{ "& .MuiDrawer-paper":{ width:DRAWER_WIDTH, bgcolor:APP_COLORS.drawerBg, borderRight:`1px solid ${APP_COLORS.border}` } }}>
                    <Box sx={{ display:"flex", justifyContent:"flex-end", p:0.5 }}>
                        <IconButton size="small" onClick={() => setMobileDrawerOpen(false)} sx={{ color:APP_COLORS.textMuted }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    {drawerContent}
                </Drawer>
            )}
            {/* Main content */}
            <Box component="main" sx={{ flexGrow:1, mt:isMobile?"48px":0, minWidth:0, overflowX:"hidden" }}>
                <Box sx={{ maxWidth:1100, mx:"auto", p:{ xs:2, sm:3 } }}>
                    {/* Desktop header */}
                    {!isMobile && (
                        <Box sx={{ display:"flex", alignItems:"center", gap:2, mb:3, pb:2, borderBottom:`1px solid ${APP_COLORS.border}` }}>
                            <Typography sx={{ fontSize:24 }}>{activeTopic?.data.icon}</Typography>
                            <Box>
                                <Box sx={{ display:"flex", alignItems:"center", gap:1.5 }}>
                                    <Box sx={{ background:"linear-gradient(135deg,#3776ab,#ffd343)", borderRadius:0.5, px:0.75, py:0.2 }}>
                                        <Typography sx={{ fontWeight:900, fontSize:10, color:"#fff", lineHeight:1 }}>Py</Typography>
                                    </Box>
                                    <Typography sx={{ fontWeight:900, fontSize:18, color:APP_COLORS.title }}>{activeTopic?.label}</Typography>
                                </Box>
                                <Typography sx={{ fontSize:11, color:APP_COLORS.textMuted }}>Python Complete Algorithms — Data Structures &amp; Patterns</Typography>
                            </Box>
                        </Box>
                    )}
                    {/*
           * key={activeTopicId} forces React to fully UNMOUNT and REMOUNT
           * TopicContent on topic change — resets all internal tab/filter
           * state without lifting state up to this component.
           */}
                    {activeTopic && (
                        <TopicContent key={activeTopicId} topicData={activeTopic.data} copiedId={copiedId} onCopy={handleCopy} />
                    )}
                </Box>
            </Box>
        </Box>
    );
};

/**
 * PythonCompleteAlgorithms — DEFAULT EXPORT
 * Wraps everything in ThemeProvider + CssBaseline.
 * useMemo prevents the theme from being recreated on every render.
 */
function PythonCompleteAlgorithms() {
    const appTheme = useMemo(() => buildAppTheme(), []);
    return (
        <ThemeProvider theme={appTheme}>
            <CssBaseline />
            <AppContent />
        </ThemeProvider>
    );
}

export default PythonCompleteAlgorithms;
